/**
 * SDMS Result Validation — Excel-style spreadsheet popup (Phase 4).
 *
 * Opens from the Open Package detail view's "SDMS" toolbar button.
 * Mirrors the prototype's `openSdms()` markup and behaviour
 * (SDMS-Result-Validation-Prototype.html, Step 3) and the live ALPACA
 * LIMS SDMS popup screenshot signed off on 2026-06-30.
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ SDMS                                                          ✕ │  ← window title
 *   │ SDMS · File · Home · Insert · PageLayout · Formulas · Data · …  │  ← ribbon tabs
 *   │ Mode/Test/QCBatchID | Retrieve  Submit  Save | New Cal  Attach… │  ← ribbon
 *   │ [A1]   ✕ ✓   formula bar                                         │
 *   │   A    B    C    D ... R                                         │
 *   │ 1                                              {Test}            │
 *   │ 2 Test  {Test}  Spike Conc. (mg/L)  {n}  CCV Low of STD …        │
 *   │ ...                                                              │
 *   │ Sheet1  Reagents  Instruments  Raw Data  +                       │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * Phase 4 scope:
 *   • View mode by default — Edit-only ribbon buttons stay disabled.
 *   • Wired actions: Close, Full Screen, Project Notes, Audit Trail, Rollback.
 *   • Every other ribbon button shows an inline "stubbed" toast.
 *   • The spreadsheet body renders the batch's own metadata in the header
 *     rows + a TEST-SPECIFIC seed of representative result rows.  Per-test
 *     presets (Alkalinity, Anions/IC, generic) drive the columns + header
 *     fields + sample rows so an Alkalinity batch looks like Alkalinity in
 *     legacy.  Phase 5 swaps the seed for real per-batch SDMS rows via a
 *     dedicated SP.
 *   • Sheet1 is the only real tab; the others toast "stubbed".
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ExcelJS from 'exceljs';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { api, runWithAuditReason } from '../../api/client';
// Univer is heavy (~MBs) and only needed when the SDMS sheet is open, so it is
// code-split out of the main bundle and loaded on demand.
const SdmsUniverSheet = lazy(() => import('./SdmsUniverSheet').then(m => ({ default: m.SdmsUniverSheet })));
import type { SdmsUniverHandle } from './SdmsUniverSheet';
import type { SdmsDfOverlay } from './sdmsDfOverlay';
// Namespace import: `numfmt` is CJS with named exports and no default. A default import
// type-checks and then fails the BUNDLE ("numfmt has no default export").
import * as numfmtNs from 'numfmt';
import type { AnchorBox, ChartSeries } from './sdmsChartAnchor';
import { niceAxis, readChartSeries } from './sdmsChartAnchor';
import type { SdmsBatch } from './SdmsValidationActions';
import { RollbackModal } from './SdmsValidationActions';
import { NotesListPopup, useNotesCount } from './SdmsProjectNotes';
import { AuditTrailPopup } from '../../components/AuditTrailButton';
import { isAuditEnabled, useAuditTrailConfig } from '../../utils/auditConfig';
import { useConfirm } from '../../components/confirm/useConfirm';
import { showDialog } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
import './SdmsSpreadsheet.css';

// ─────────────────────────────────────────────────────────────────────────
//   LIVE workbook (ExcelJS) — the real ALPACA SDMS .xlsx for this batch
// ─────────────────────────────────────────────────────────────────────────
// Fetches GET /result-validation/{batchOid}/sdms-workbook (the batch's saved
// sheet, else the empty template for its TemplateID) and parses it read-only
// with ExcelJS (MIT).  Unlike SheetJS community, ExcelJS reads cell STYLING —
// fills, fonts, borders, alignment, merges, column widths — so the view is a
// faithful match of ALPACA's SDMS sheet (yellow input cells, green QC
// highlights, bold labels), not just the data.  Renders the REAL template +
// REAL data for EVERY template, replacing the 3 hardcoded presets.  Presets
// remain only as a fallback when the API returns 204 (no sheet on file).
//
// Read-only by design (matches ALPACA's View mode on the Validation screen).

/** One rendered cell — display text + the styles ExcelJS surfaced. */
interface LiveCell {
  text:  string;
  /** The cell's Excel number format. The calibration chart labels its axes with the format
   *  of the CELLS IT PLOTS — Excel links axis labels to the source unless the chart says
   *  otherwise, which is why LIMS shows "0, 3 … 21" on Color (General) and
   *  "0.0000 … 1.0000" on Phosphate ("0.0000"). Verified on both via DevExpress. */
  numFmt?: string;
  bg?:   string;        // fill colour  → CSS background
  color?: string;       // font colour  → CSS color
  bold?: boolean;
  size?: number;        // font size in POINTS (title / label sizing parity)
  align?: 'left' | 'center' | 'right';
  cs?:   number;        // colspan (merge master only)
  rs?:   number;        // rowspan (merge master only)
  // Per-side CSS border from the workbook (ExcelJS cell.border).  The data
  // range carries real (dark) borders in the ALPACA template; empty cells have
  // none → they fall back to the faint default gridline.  undefined = no
  // workbook border on that side.
  bt?:   string;
  br?:   string;
  bb?:   string;
  bl?:   string;
}
interface LiveGrid {
  colLetters:  string[];               // real Excel letters of VISIBLE cols (gapped, like LIMS)
  colWidths:   number[];               // px per visible column
  rowNums:     number[];               // real Excel row numbers of VISIBLE rows
  cells:       (LiveCell | null)[][];  // cells[r][c] over visible cols; null = covered by a merge
  sheetNames:  string[];
  activeSheet: string;
}

// ── value formatting ────────────────────────────────────────────────────
// ExcelJS's cell.text does NOT apply the cell's number format to formula
// results or dates, and leaks the "_x000d_" carriage-return escape into
// multi-line header strings.  We format ourselves so the sheet reads exactly
// like ALPACA (18.84 not 18.837600000000002; 01/15/2026 not a JS Date dump;
// "Sample\nVolume\n(ml)" not "Sample_x000d_ Volume…").

/** Decode OOXML's `_xHHHH_` control-character escapes, then drop CR; \n line breaks are
 *  KEPT (rendered via white-space: pre-line) so multi-line headers stack.
 *  Handling only the CR form left the LINE-FEED form visible on screen as
 *  "Numeric_x000a_Result_x000a_(mg/L)" (client 2026-08-24). `_x005f_` is OOXML's escape
 *  for a literal underscore that would otherwise start an escape run — decoding it in the
 *  same pass leaves the rest of that run as literal text, which is the intent. */
function cleanStr(s: string): string {
  return s
    .replace(/_x([0-9A-Fa-f]{4})_/g, (_m, hex: string) => {
      const code = parseInt(hex, 16);
      return code === 0x5f ? '_' : String.fromCharCode(code);
    })
    .replace(/\r/g, '');
}

/** Decimal places declared in an Excel number format (first section).
 *  "0.00"→2, "#,##0.0"→1, "0"→0, "General"/"@"/undefined→null (no rounding). */
function decimalsFromNumFmt(fmt: string | undefined): number | null {
  if (!fmt || fmt === 'General' || fmt === '@') return null;
  const sect = fmt.split(';')[0] ?? '';
  const dot = sect.indexOf('.');
  if (dot < 0) return 0;
  let n = 0;
  for (let i = dot + 1; i < sect.length; i++) {
    const ch = sect[i];
    if (ch === '0' || ch === '#') n++; else break;
  }
  return n;
}

function fmtNumber(v: number, fmt: string | undefined): string {
  // Percent format ("0%", "0.0%"): Excel stores 73% as 0.73 → ×100 + "%".
  if (fmt && fmt.includes('%')) {
    const pd = decimalsFromNumFmt(fmt) ?? 0;
    // Kill IEEE float noise BEFORE rounding: 0.895*100 = 89.49999999999999,
    // which toFixed(0) would floor to "89" — but Excel shows 90% (89.5 rounds
    // half-away-from-zero).  toPrecision(12) restores 89.5 so toFixed gives 90%.
    const pct = parseFloat((v * 100).toPrecision(12));
    return `${pct.toFixed(pd)}%`;
  }
  const d = decimalsFromNumFmt(fmt);
  if (d == null) {
    // General → kill IEEE float noise (18.837600000000002 → 18.8376).
    return String(parseFloat(v.toPrecision(12)));
  }
  const s = v.toFixed(d);
  if (fmt && fmt.includes(',')) {   // thousands grouping
    const [int, dec] = s.split('.');
    const grp = (int ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return dec ? `${grp}.${dec}` : grp;
  }
  return s;
}

const pad2 = (n: number) => String(n).padStart(2, '0');
/** Excel date/time → display string, honouring the cell's number format:
 *    • time-only format (h/s tokens, no y/d)  → "HH:mm" (":ss" if the fmt has s)
 *    • date + time                            → "MM/DD/YYYY HH:mm"
 *    • date-only                              → "MM/DD/YYYY"
 *  Excel stores a TIME-ONLY value on its epoch date (1899-12-30), so without
 *  this a time like 11:56 would render as "12/30/1899 11:56".  When the format
 *  is ambiguous (General/unknown) we fall back on the epoch heuristic: a 1899
 *  date means it's really a time.  UTC components avoid a timezone day-shift. */
function fmtDate(d: Date, fmt: string | undefined): string {
  const f = fmt ?? '';
  const hasTime = /[hs]/i.test(f);
  const hasDate = /[yd]/i.test(f);
  const isEpoch = d.getUTCFullYear() === 1899;   // Excel time-only epoch
  const time = () => {
    const t = `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
    return /s/i.test(f) ? `${t}:${pad2(d.getUTCSeconds())}` : t;
  };
  const date = () => `${pad2(d.getUTCMonth() + 1)}/${pad2(d.getUTCDate())}/${d.getUTCFullYear()}`;

  // Epoch date (1899-12-30) is Excel's TIME-ONLY sentinel — show time only even
  // when the cell's format also carries date tokens (LIMS does the same).
  if (isEpoch) return time();
  if (hasTime && !hasDate) return time();
  if (hasDate && hasTime)  return `${date()} ${time()}`;
  if (hasDate && !hasTime) return date();
  return date();
}

/** Recursively resolve any ExcelJS cell value to its display string —
 *  never emits "[object Object]".  Handles: number, date, boolean, string,
 *  rich text, hyperlink, error, and formula/shared-formula (recurses into the
 *  cached result; blank when a formula has no cached result). */
function toDisplay(v: unknown, numFmt: string | undefined): string {
  if (v == null) return '';
  if (v instanceof Date)       return fmtDate(v, numFmt);
  if (typeof v === 'number')   return fmtNumber(v, numFmt);
  if (typeof v === 'boolean')  return v ? 'TRUE' : 'FALSE';
  if (typeof v === 'string')   return cleanStr(v);
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (Array.isArray(o.richText))
      return cleanStr((o.richText as Array<{ text?: string }>).map(t => t.text ?? '').join(''));
    if ('error'  in o) return String(o.error);
    if ('result' in o) return toDisplay(o.result ?? 0, numFmt);   // formula → cached result (null = DevExpress omits <v>0</v> for zero)
    if ('text'   in o) return cleanStr(String(o.text));       // hyperlink
    return '';   // formula/sharedFormula with no cached result → blank
  }
  return '';
}

/** Read + format one cell's display value the way ALPACA shows it. */
function fmtCell(cell: ExcelJS.Cell): string {
  return toDisplay(cell.value, cell.numFmt);
}

// Standard Office theme palette (2013+).  ExcelJS's fill `theme` index uses
// the UI order with 0/1 = background/text swapped from the file order:
// 0=lt1(white) 1=dk1(black) 2=lt2 3=dk2 4=accent1 … 9=accent6.
const THEME_RGB: Record<number, [number, number, number]> = {
  0: [255, 255, 255], 1: [0, 0, 0],   2: [231, 230, 230], 3: [68, 84, 106],
  4: [68, 114, 196],  5: [237, 125, 49], 6: [165, 165, 165], 7: [255, 192, 0],
  8: [91, 155, 213],  9: [112, 173, 71],           // accent6 = green
};
// Classic Excel 56-colour indexed palette (only the entries these templates
// actually use are listed; unknown → null so we skip the fill).
const INDEXED_RGB: Record<number, [number, number, number]> = {
  9: [255, 255, 255], 64: [0, 0, 0], 10: [255, 0, 0], 11: [0, 255, 0],
  13: [255, 255, 0],  42: [204, 255, 255], 43: [204, 255, 204],   // 43 = light green
  35: [204, 255, 204], 50: [153, 204, 0],  17: [255, 255, 204],
};

/** Apply an Excel tint to an RGB channel (0..255). tint>0 lightens, <0 darkens. */
function applyTint(ch: number, tint: number): number {
  if (!tint) return ch;
  return tint < 0
    ? Math.round(ch * (1 + tint))
    : Math.round(ch * (1 - tint) + 255 * tint);
}
const toHex = ([r, g, b]: [number, number, number]) =>
  '#' + [r, g, b].map(x => Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0')).join('').toUpperCase();

/** Resolve any ExcelJS colour (ARGB, theme+tint, or indexed) → CSS "#RRGGBB",
 *  or null.  This is what surfaces the green QC fills (theme accent6) that
 *  plain ARGB parsing missed.  ExcelJS's Color TS type omits tint/indexed
 *  (present at runtime), so we read via a loose shape. */
function resolveColor(
  c: { argb?: string; theme?: number; tint?: number; indexed?: number } | undefined,
): string | null {
  if (!c) return null;
  if (typeof c.argb === 'string' && c.argb.length >= 6)
    return `#${c.argb.length === 8 ? c.argb.slice(2) : c.argb}`.toUpperCase();
  if (typeof c.theme === 'number' && THEME_RGB[c.theme]) {
    const base = THEME_RGB[c.theme]!;
    const t = c.tint ?? 0;
    return toHex([applyTint(base[0], t), applyTint(base[1], t), applyTint(base[2], t)]);
  }
  if (typeof c.indexed === 'number' && INDEXED_RGB[c.indexed])
    return toHex(INDEXED_RGB[c.indexed]!);
  return null;
}

/** One ExcelJS border side → a CSS border string, or undefined when the side
 *  has no border.  ALPACA's SDMS templates apply real borders to the DATA range
 *  (header + result rows), so rendering them reproduces the "bold lines around
 *  the data" look; empty cells (no border) fall back to the faint gridline.
 *  Excel weights map: hair/thin → 1px, medium/double/thick → 2px.  A stored
 *  black border is softened to a dark grey so it reads like LIMS, not harsh. */
function borderCss(
  side: { style?: string; color?: { argb?: string; theme?: number; tint?: number; indexed?: number } } | undefined,
): string | undefined {
  const s = side?.style;
  if (!s) return undefined;
  const width = (s === 'thick' || s === 'medium' || s === 'double') ? 2 : 1;
  const style =
    (s === 'dashed' || s === 'mediumDashed' || s === 'dashDot' || s === 'dashDotDot' || s === 'slantDashDot') ? 'dashed' :
    (s === 'dotted' || s === 'hair') ? 'dotted' :
    (s === 'double') ? 'double' : 'solid';
  let color = resolveColor(side!.color) ?? '#585858';
  if (color === '#000000') color = '#555555';
  return `${width}px ${style} ${color}`;
}

/** Excel column width (≈ chars) → px.  ~7 px/char + padding, matching how
 *  the ALPACA sheet proportions read. */
function widthToPx(w: number | undefined): number {
  if (!w || w <= 0) return 84;
  return Math.round(w * 7 + 5);
}

// ── conditional formatting (the green QC highlights) ──────────────────────
// ExcelJS reads CF RULES but never applies them to cells, so the green "%Rec
// within limits" / "Pass" fills don't come through as static fills.  We
// evaluate the common rule types (cellIs numeric compare/between, containsText,
// equal-text) against each cell in the rule's range and apply the rule's dxf
// fill/font.  Expression / colour-scale / data-bar / icon-set rules are not
// evaluated (would need a formula engine).

/** Raw numeric value of a cell (formula result or literal number), else null. */
function rawNum(cell: ExcelJS.Cell): number | null {
  let v: unknown = cell.value;
  if (v && typeof v === 'object' && 'result' in (v as Record<string, unknown>))
    v = (v as Record<string, unknown>).result ?? 0;   // null = DevExpress omits <v>0</v> for zero
  return typeof v === 'number' ? v : null;
}

/** dxf (differential) solid fills carry the highlight colour in bgColor, so
 *  prefer it, then fgColor. */
function dxfFillColor(fill: { bgColor?: unknown; fgColor?: unknown } | undefined): string | null {
  if (!fill) return null;
  return resolveColor(fill.bgColor as never) ?? resolveColor(fill.fgColor as never) ?? null;
}

/** "N8:N24" → range; single "N8" → 1×1 range. */
function parseA1Range(ref: string): { r1: number; c1: number; r2: number; c2: number } | null {
  const [pa, pb] = ref.split(':');
  const a = cellRef(pa ?? ''); if (!a) return null;
  const b = pb ? cellRef(pb) : a; if (!b) return null;
  return { r1: Math.min(a.r, b.r), c1: Math.min(a.c, b.c), r2: Math.max(a.r, b.r), c2: Math.max(a.c, b.c) };
}

interface CfRule { type?: string; operator?: string; formulae?: unknown[]; text?: string; priority?: number; style?: { fill?: { bgColor?: unknown; fgColor?: unknown }; font?: { color?: unknown } } }

/** Letters → 1-based column number ("N" → 14, "AA" → 27). */
function colToNum(letters: string): number {
  let c = 0; for (const ch of letters.toUpperCase()) c = c * 26 + (ch.charCodeAt(0) - 64); return c;
}

/** Resolve a CF formula operand to a number: a numeric literal, or a cell
 *  reference (relative refs shift by the cell's offset (dr,dc) from the CF
 *  range's anchor; $-anchored parts stay put).  null when unresolvable. */
function refToNum(token: string, ws: ExcelJS.Worksheet, dr: number, dc: number): number | null {
  const t = token.trim().replace(/^=/, '');
  if (t === '') return null;
  if (/^-?\d+(\.\d+)?$/.test(t)) return parseFloat(t);
  const m = /^(\$?)([A-Za-z]+)(\$?)(\d+)$/.exec(t);
  if (!m) return null;
  const col = colToNum(m[2]!) + (m[1] === '$' ? 0 : dc);
  const rowN = parseInt(m[4]!, 10) + (m[3] === '$' ? 0 : dr);
  if (col < 1 || rowN < 1) return null;
  return rawNum(ws.getRow(rowN).getCell(col));
}

/** Evaluate a CF `expression` rule formula to a boolean for one cell.  Compact
 *  recursive-descent parser supporting AND()/OR()/NOT(), comparisons
 *  (>= <= <> = > <), numeric literals, and (relative) cell references — which
 *  covers the QC "within limits" rules these templates use
 *  (e.g. =AND(N7>=90,N7<=110) or =AND(N7>=$O7,N7<=$P7)). Unknown constructs
 *  → false (rule simply doesn't fire). */
function evalCfExpression(formula: string, ws: ExcelJS.Worksheet, dr: number, dc: number): boolean {
  const src = formula.trim().replace(/^=/, '');
  let pos = 0;
  const ws_ = () => { while (pos < src.length && /\s/.test(src[pos]!)) pos++; };
  const eat = (s: string) => { ws_(); if (src.slice(pos, pos + s.length).toUpperCase() === s.toUpperCase()) { pos += s.length; return true; } return false; };

  function parseBool(): boolean {
    ws_();
    if (eat('AND(')) { const a = parseArgs(); eat(')'); return a.every(Boolean); }
    if (eat('OR('))  { const a = parseArgs(); eat(')'); return a.some(Boolean); }
    if (eat('NOT(')) { const v = parseBool(); eat(')'); return !v; }
    if (eat('(')) {
      // could be a grouped bool or a grouped number comparison
      const save = pos;
      try { const v = parseBool(); if (eat(')')) return v; } catch { /* fall back */ }
      pos = save;
    }
    // comparison: operand OP operand
    const left = parseNum();
    ws_();
    const op = parseComparator();
    if (!op) return !!left;               // bare operand → truthy
    const right = parseNum();
    if (left == null || right == null) return false;
    switch (op) {
      case '>=': return left >= right;
      case '<=': return left <= right;
      case '<>': return left !== right;
      case '=':  return left === right;
      case '>':  return left > right;
      case '<':  return left < right;
      default:   return false;
    }
  }
  function parseArgs(): boolean[] {
    const out: boolean[] = [];
    do { out.push(parseBool()); ws_(); } while (eat(','));
    return out;
  }
  function parseComparator(): string | null {
    ws_();
    for (const op of ['>=', '<=', '<>', '=', '>', '<']) if (src.startsWith(op, pos)) { pos += op.length; return op; }
    return null;
  }
  function parseNum(): number | null {
    ws_();
    // read a token up to the next comparator / comma / paren
    const start = pos;
    while (pos < src.length && !/[,()]/.test(src[pos]!) && !['>=','<=','<>','=','>','<'].some(o => src.startsWith(o, pos))) pos++;
    return refToNum(src.slice(start, pos), ws, dr, dc);
  }

  try { return parseBool(); } catch { return false; }
}

/** Does one CF rule fire for this cell?  Handles cellIs (numeric ops + between,
 *  with literal OR cell-reference bounds), containsText, and expression rules.
 *  (dr,dc) is the cell's offset from the CF range anchor for relative refs. */
function cfRuleMatches(rule: CfRule, cell: ExcelJS.Cell, ws: ExcelJS.Worksheet, dr: number, dc: number): boolean {
  if (rule.type === 'expression') {
    const f = (rule.formulae ?? [])[0];
    return typeof f === 'string' && f !== '' ? evalCfExpression(f, ws, dr, dc) : false;
  }
  if (rule.type === 'cellIs') {
    const f = (rule.formulae ?? []).map(x => String(x));
    const isQuoted = (s: string) => /^\s*".*"\s*$/.test(s);
    if ((rule.operator === 'equal' || rule.operator === 'notEqual') && f[0] && isQuoted(f[0])) {
      const target = f[0].trim().replace(/^"|"$/g, '').toLowerCase();
      const txt = fmtCell(cell).toLowerCase();
      return rule.operator === 'equal' ? txt === target : txt !== target;
    }
    const v = rawNum(cell); if (v == null) return false;
    const a = refToNum(f[0] ?? '', ws, dr, dc);
    const b = refToNum(f[1] ?? '', ws, dr, dc);
    switch (rule.operator) {
      case 'between':            return a != null && b != null && v >= Math.min(a, b) && v <= Math.max(a, b);
      case 'notBetween':         return a != null && b != null && !(v >= Math.min(a, b) && v <= Math.max(a, b));
      case 'greaterThan':        return a != null && v > a;
      case 'lessThan':           return a != null && v < a;
      case 'greaterThanOrEqual': return a != null && v >= a;
      case 'lessThanOrEqual':    return a != null && v <= a;
      case 'equal':              return a != null && v === a;
      case 'notEqual':           return a != null && v !== a;
      default:                   return false;
    }
  }
  if (rule.type === 'containsText' && (!rule.operator || rule.operator === 'containsText')) {
    const needle = String(rule.text ?? '').toLowerCase();
    return needle !== '' && fmtCell(cell).toLowerCase().includes(needle);
  }
  return false;
}

/** Map of CF-driven overrides keyed by absolute "r,c" → {bg,color}.  Highest
 *  priority (lowest number) matching rule wins. */
function buildCfOverrides(ws: ExcelJS.Worksheet): Map<string, { bg?: string; color?: string }> {
  const acc = new Map<string, { bg?: string; color?: string; prio: number }>();
  const cfs = (ws as unknown as { conditionalFormattings?: Array<{ ref?: string; rules?: CfRule[] }> }).conditionalFormattings
    ?? (ws.model as unknown as { conditionalFormattings?: Array<{ ref?: string; rules?: CfRule[] }> })?.conditionalFormattings;
  if (!Array.isArray(cfs)) return new Map();
  for (const cf of cfs) {
    const ranges = String(cf.ref ?? '').split(/[\s,]+/).filter(Boolean)
      .map(parseA1Range).filter(Boolean) as Array<{ r1: number; c1: number; r2: number; c2: number }>;
    if (!ranges.length) continue;
    // Relative-reference anchor = top-left of the CF range (Excel's active cell
    // when the rule was authored).  Per-cell offset (dr,dc) shifts relative
    // refs in the rule formula.
    const anchorR = Math.min(...ranges.map(r => r.r1));
    const anchorC = Math.min(...ranges.map(r => r.c1));
    for (const rule of cf.rules ?? []) {
      const bg = dxfFillColor(rule.style?.fill);
      const color = resolveColor(rule.style?.font?.color as never);
      if (!bg && !color) continue;
      const prio = typeof rule.priority === 'number' ? rule.priority : 999;
      for (const rg of ranges)
        for (let r = rg.r1; r <= rg.r2; r++)
          for (let c = rg.c1; c <= rg.c2; c++) {
            if (!cfRuleMatches(rule, ws.getRow(r).getCell(c), ws, r - anchorR, c - anchorC)) continue;
            const key = `${r},${c}`;
            const prev = acc.get(key);
            if (!prev || prio < prev.prio)
              acc.set(key, { bg: bg ?? prev?.bg, color: color ?? prev?.color, prio });
          }
    }
  }
  const out = new Map<string, { bg?: string; color?: string }>();
  for (const [k, v] of acc) out.set(k, { bg: v.bg, color: v.color });
  return out;
}

/** Parse an .xlsx ArrayBuffer into a styled read-only grid for the requested
 *  sheet (defaults to the first).  Async — ExcelJS load returns a Promise.
 *  Returns null when the workbook has no usable sheet. */
async function parseWorkbook(buf: ArrayBuffer, sheetName?: string): Promise<LiveGrid | null> {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  // Only VISIBLE sheets become tabs — ALPACA hides lookup sheets (e.g.
  // "Qualifier", the qualifier-code list used for dropdowns) via the
  // worksheet's hidden state, so DevExpress never shows them as a tab.
  const visibleWs = wb.worksheets.filter(w => ((w.state as string | undefined) ?? 'visible') === 'visible');
  const names = visibleWs.map(w => w.name);
  const ws = (sheetName && names.includes(sheetName))
    ? wb.getWorksheet(sheetName)
    : (visibleWs[0] ?? wb.worksheets[0]);
  if (!ws) return null;

  const totalCols = Math.max(1, ws.columnCount);
  const totalRows = Math.max(1, ws.rowCount);

  // VISIBLE columns/rows only — ALPACA (DevExpress) hides the template's
  // storage/calc columns (SystemID, uqSampleID, RunNo, *LIMIT, …) and shows
  // gapped column letters (A B C E F G …).  Respect the hidden flag to match.
  const visCols: number[] = [];
  for (let c = 1; c <= totalCols; c++) if (!ws.getColumn(c).hidden) visCols.push(c);
  const visColSet = new Set(visCols);
  const visRows: number[] = [];
  for (let r = 1; r <= totalRows; r++) if (!ws.getRow(r).hidden) visRows.push(r);
  const visRowSet = new Set(visRows);

  // Count visible cols/rows inside an absolute range so colspan/rowspan stay
  // correct after hidden columns/rows are dropped.
  const visColsInRange = (c1: number, c2: number) => {
    let n = 0; for (let c = c1; c <= c2; c++) if (visColSet.has(c)) n++; return Math.max(1, n);
  };
  const visRowsInRange = (r1: number, r2: number) => {
    let n = 0; for (let r = r1; r <= r2; r++) if (visRowSet.has(r)) n++; return Math.max(1, n);
  };

  // Merge map — hidden-aware.  A merge's value lives in its top-left MASTER,
  // but that master can sit in a hidden column (e.g. yellow input "50" whose
  // master is hidden col D).  ALPACA renders the value in the first VISIBLE
  // cell of the merge, so we:
  //   • pick the display position = (first visible row, first visible col) in range,
  //   • pull the value/style from the ORIGINAL master cell,
  //   • cover every other visible cell in the range.
  // Without this the value vanishes (master hidden + rest covered).
  const cover   = new Set<string>();                                  // absolute cells → render null
  const display = new Map<string, { srcR: number; srcC: number; cs: number; rs: number }>();  // "r,c" → source + span
  const merges: string[] = (ws.model?.merges as string[] | undefined) ?? [];
  for (const rangeStr of merges) {
    const [a, b] = rangeStr.split(':');
    if (!a || !b) continue;
    const s = cellRef(a), e = cellRef(b);
    if (!s || !e) continue;
    let fvc = -1; for (let c = s.c; c <= e.c; c++) if (visColSet.has(c)) { fvc = c; break; }
    let fvr = -1; for (let r = s.r; r <= e.r; r++) if (visRowSet.has(r)) { fvr = r; break; }
    if (fvc < 0 || fvr < 0) {                     // whole merge hidden → cover all
      for (let r = s.r; r <= e.r; r++) for (let c = s.c; c <= e.c; c++) cover.add(`${r},${c}`);
      continue;
    }
    display.set(`${fvr},${fvc}`, {
      srcR: s.r, srcC: s.c,
      cs: visColsInRange(s.c, e.c),
      rs: visRowsInRange(s.r, e.r),
    });
    for (let r = s.r; r <= e.r; r++)
      for (let c = s.c; c <= e.c; c++)
        if (!(r === fvr && c === fvc)) cover.add(`${r},${c}`);
  }

  const colLetters = visCols.map(colLetter);
  const colWidths  = visCols.map(c => widthToPx(ws.getColumn(c).width));

  // Conditional-formatting overrides (the green QC highlights).
  const cfOverrides = buildCfOverrides(ws);

  const cells: (LiveCell | null)[][] = [];
  for (const r of visRows) {
    const row: (LiveCell | null)[] = [];
    for (const c of visCols) {
      if (cover.has(`${r},${c}`)) { row.push(null); continue; }   // covered by a merge
      const dm   = display.get(`${r},${c}`);
      const srcR = dm ? dm.srcR : r, srcC = dm ? dm.srcC : c;
      // Read value + style from the merge master (may be a hidden cell) when
      // this is a merge display position; otherwise from the cell itself.
      const cell = ws.getRow(srcR).getCell(srcC);
      const fill = cell.fill as ExcelJS.FillPattern | undefined;
      const staticBg = fill?.type === 'pattern' && fill.pattern === 'solid'
        ? resolveColor(fill.fgColor) : null;
      const font = cell.font;
      const al   = cell.alignment?.horizontal;
      const bd   = cell.border;   // real workbook borders (the data-range grid)
      // CF override wins over the static fill (that's how the green appears).
      const cf   = cfOverrides.get(`${srcR},${srcC}`);
      const bg   = cf?.bg ?? (staticBg && staticBg !== '#FFFFFF' ? staticBg : undefined);
      row.push({
        text:  fmtCell(cell),
        numFmt: cell.numFmt,
        bg,
        color: cf?.color ?? resolveColor(font?.color) ?? undefined,
        bold:  !!font?.bold,
        size:  typeof font?.size === 'number' ? font.size : undefined,
        align: al === 'right' || al === 'center' || al === 'left' ? al : undefined,
        cs:    dm && dm.cs > 1 ? dm.cs : undefined,
        rs:    dm && dm.rs > 1 ? dm.rs : undefined,
        bt:    borderCss(bd?.top),
        br:    borderCss(bd?.right),
        bb:    borderCss(bd?.bottom),
        bl:    borderCss(bd?.left),
      });
    }
    cells.push(row);
  }

  applyQcHighlights(cells);

  // ── Fill the Excel canvas (LIMS / DevExpress parity) ──────────────────
  // ALPACA's SDMS view shows the FULL gridded canvas — empty columns out to
  // ~AG and empty rows below the data, not just the used range.  Pad the
  // visible grid with empty cells so the sheet reads like Excel (faint
  // gridlines continuing to the right and below) instead of stopping abruptly
  // at the last populated column/row.  Purely presentational — runs AFTER
  // merges / CF / QC highlighting so none of that logic is disturbed.
  const MIN_COLS = 33;          // out to column AG, like the legacy screenshot
  const MIN_ROWS = 42;
  const PAD_COL_PX = 64;        // Excel's default column width in px
  let nextColNum = (visCols.length ? visCols[visCols.length - 1]! : 0) + 1;
  while (colLetters.length < MIN_COLS) {
    colLetters.push(colLetter(nextColNum));
    colWidths.push(PAD_COL_PX);
    nextColNum++;
  }
  const canvasCols = colLetters.length;
  // Extend every existing row with empty trailing cells so widths line up.
  for (const row of cells) while (row.length < canvasCols) row.push({ text: '' });
  // Append empty trailing rows below the data.
  const rowNums = [...visRows];
  let nextRowNum = (visRows.length ? visRows[visRows.length - 1]! : 0) + 1;
  while (rowNums.length < MIN_ROWS) {
    cells.push(Array.from({ length: canvasCols }, () => ({ text: '' } as LiveCell)));
    rowNums.push(nextRowNum);
    nextRowNum++;
  }

  return { colLetters, colWidths, rowNums, cells, sheetNames: names, activeSheet: ws.name };
}

/** ALPACA-parity QC highlighting, derived deterministically from the visible
 *  columns (ExcelJS's conditional-formatting parse is unreliable for the
 *  DevExpress-saved books, so we replicate the cues rather than depend on it):
 *    • QCStatus = "Pass" → green (Excel "Good" green), "Fail" → red.
 *    • Rec % / RPD % within their control limit → green.
 *  Header row is located by its "SysSampleCode" cell; special columns are
 *  matched by header text so it works across every template that uses these
 *  standard QC columns. */
function applyQcHighlights(cells: (LiveCell | null)[][]): void {
  const GREEN = '#C6EFCE', GREEN_TX = '#006100', RED = '#FFC7CE', RED_TX = '#9C0006';
  const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();

  // Header row: the sample sheet has "SysSampleCode"; the Calibration sheet has
  // a "%Recovery" column instead.  Match either so both sheets get highlighted.
  let hdr = -1;
  for (let i = 0; i < cells.length; i++)
    if (cells[i]!.some(c => c && (norm(c.text) === 'syssamplecode' || norm(c.text).includes('recovery')))) { hdr = i; break; }
  if (hdr < 0) return;

  const H = cells[hdr]!;
  const find = (pred: (h: string) => boolean) => H.findIndex(c => (c ? pred(norm(c.text)) : false));
  const qcCol  = find(h => h === 'qcstatus');
  const recCol = find(h => h.includes('rec') && h.includes('%') && !h.includes('limit') && !h.includes('ctrl'));
  const rpdCol = find(h => h.includes('rpd') && h.includes('%') && !h.includes('limit') && !h.includes('ctrl'));
  const recLim = find(h => h.includes('rec') && (h.includes('limit') || h.includes('ctrl')));
  const rpdLim = find(h => h.includes('rpd') && (h.includes('limit') || h.includes('ctrl')));
  // Calibration sheet's "%Recovery" column — LIMS greens every computed
  // recovery (the #N/A rows stay plain).  Distinct from the sample sheet's
  // "Rec %" (which is limit-checked above); "%recovery" ≠ "rec%".
  const recoveryCol = find(h => h.includes('recovery'));

  const num = (t: string): number | null => { const n = parseFloat(t); return isNaN(n) ? null : n; };
  const range = (t: string): [number, number] | null => {
    const m = t.match(/(-?\d+(?:\.\d+)?)\s*[-–]\s*(-?\d+(?:\.\d+)?)/);   // "90 - 110"
    return m ? [parseFloat(m[1]!), parseFloat(m[2]!)] : null;
  };
  const within = (valCol: number, limCol: number, row: (LiveCell | null)[]) => {
    if (valCol < 0) return;
    const cell = row[valCol]; if (!cell) return;
    const v = num(cell.text); if (v == null) return;
    const limTxt = limCol >= 0 ? (row[limCol]?.text ?? '') : '';
    const rng = range(limTxt);
    let ok = false;
    if (rng) ok = v >= Math.min(...rng) && v <= Math.max(...rng);
    else { const single = num(limTxt); if (single != null) ok = v <= single; }   // single = upper bound
    if (ok) cell.bg = GREEN;
  };

  for (let i = hdr + 1; i < cells.length; i++) {
    const row = cells[i]!;
    if (qcCol >= 0 && row[qcCol]) {
      const v = row[qcCol]!.text.trim().toLowerCase();
      if (v === 'pass')      { row[qcCol]!.bg = GREEN; row[qcCol]!.color = GREEN_TX; }
      else if (v === 'fail') { row[qcCol]!.bg = RED;   row[qcCol]!.color = RED_TX; }
    }
    within(recCol, recLim, row);
    within(rpdCol, rpdLim, row);
    // Calibration %Recovery — green when a numeric recovery was computed.
    if (recoveryCol >= 0 && row[recoveryCol] && num(row[recoveryCol]!.text) != null)
      row[recoveryCol]!.bg = GREEN;
  }
}

// ── Calibration curve (Recharts) ──────────────────────────────────────────
// ExcelJS can't render the embedded Excel chart, so we RECONSTRUCT the
// calibration curve from the Calibration sheet's "Known Conc" (x) and "A"
// (Absorbance, y) columns, plus the R² value shown on the sheet.

interface CalPoint { conc: number; abs: number }
interface CalData  { points: CalPoint[]; r2: string | null }

/** Pull the calibration points + R² out of a parsed Calibration sheet. */
function calibrationData(grid: LiveGrid, series?: ChartSeries | null): CalData {
  const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();
  const points: CalPoint[] = [];

  /* THE CHART NAMES ITS OWN DATA, so read it rather than guess at column headings.
     This used to look for columns literally headed "Known Conc" and "A"; the Color
     template heads them SpecifiedConc and CalculatedConc, matched nothing, and drew an
     EMPTY BOX where ALPACA draws the curve (client 2026-08-24, QB26082010_074).
     Verified against the DevExpress engine — the same one LIMS renders with — which
     reports for that batch: Arguments(X) = $A$3:$A$9, Values(Y) = $B$3:$B$9. Reading the
     chart part gives exactly that, for any template whatever its columns are called. */
  if (series?.x && series?.y) {
    const rows = Math.max(series.x.endRow - series.x.startRow, series.y.endRow - series.y.startRow) + 1;
    for (let i = 0; i < rows; i++) {
      const xc = grid.cells[series.x.startRow + i]?.[series.x.startCol];
      const yc = grid.cells[series.y.startRow + i]?.[series.y.startCol];
      const conc = parseFloat(xc?.text ?? '');
      const abs = parseFloat(yc?.text ?? '');
      if (!isNaN(conc) && !isNaN(abs)) points.push({ conc, abs });
    }
  }

  // FALLBACK: the pre-2026-08-24 header match, for a workbook whose chart part cannot be
  // read. Costs nothing when the chart was readable, since `points` is already filled.
  let hdr = -1;
  if (!points.length) {
    for (let i = 0; i < grid.cells.length; i++)
      if (grid.cells[i]!.some(c => c && norm(c.text) === 'knownconc')) { hdr = i; break; }
    if (hdr >= 0) {
      const H = grid.cells[hdr]!;
      const concCol = H.findIndex(c => c && norm(c.text) === 'knownconc');
      const absCol  = H.findIndex(c => c && norm(c.text) === 'a');
      if (concCol >= 0 && absCol >= 0) {
        for (let i = hdr + 1; i < grid.cells.length; i++) {
          const row = grid.cells[i]!;
          const conc = parseFloat(row[concCol]?.text ?? '');
          const abs  = parseFloat(row[absCol]?.text ?? '');
          if (!isNaN(conc) && !isNaN(abs)) points.push({ conc, abs });
        }
      }
    }
  }
  // R² lives in a label/value pair somewhere on the sheet ("R^2" → value cell).
  let r2: string | null = null;
  for (const row of grid.cells) {
    for (let c = 0; c < row.length; c++) {
      const t = row[c]?.text ? norm(row[c]!.text) : '';
      if (t === 'r^2' || t === 'r²' || t === 'rsquared' || t === 'r2') {
        const val = row.slice(c + 1).find(x => x && parseFloat(x.text) >= 0);
        if (val) r2 = val.text;
        break;
      }
    }
    if (r2) break;
  }
  return { points, r2 };
}


/** The reconstructed calibration-curve chart (matches ALPACA's embedded chart:
 *  Absorbance vs Known Conc line, "Series 1").  `floating` renders it to fill
 *  its parent box (the over-grid float that mirrors ALPACA's embedded chart);
 *  otherwise it renders titled + fixed-size below the grid. */
function CalibrationChart({ grid, floating, series }: { grid: LiveGrid; floating?: boolean; series?: ChartSeries | null }) {
  const { points, r2 } = useMemo(() => calibrationData(grid, series), [grid, series]);
  if (points.length < 2) return null;
  const fs = floating ? 10 : 11;
  /* Axis labels take the NUMBER FORMAT OF THE CELLS THEY PLOT — Excel links them to the
     source unless the chart overrides it, and these templates never do. That is the whole
     difference between LIMS's "0, 3 … 21" on Color (those cells are General) and
     "0.0000 … 1.0000" on Phosphate (format "0.0000"). It used to be a hardcoded 4 decimal
     places, which was right for Phosphate by luck and wrong everywhere else — Color read
     "25.0000" (client 2026-08-24). Verified on both via the DevExpress engine. */
  const yNumFmt = series?.y
    ? grid.cells[series.y.startRow]?.[series.y.startCol]?.numFmt
    : undefined;
  const axisFmt = (v: number): string => {
    const fmt = (numfmtNs as unknown as { format?: (p: string, v: number) => unknown }).format;
    if (yNumFmt && yNumFmt !== 'General' && typeof fmt === 'function') {
      try { return String(fmt(yNumFmt, v)); } catch { /* fall through to General */ }
    }
    // General: the fewest digits that still says the number.
    return String(Number(v.toFixed(10)));
  };
  // Fixed 0-based axes with evenly-spaced ticks (ALPACA parity: y 0.0000 →
  // 1.0000 by 0.2), so the slightly-negative first blank (-0.0107) can't drag
  // the axis below zero and 'auto' can't pick an ugly 1.05 max.
  /* SEVEN intervals on the value axis, not five. Checked against LIMS on both templates:
     at 5 the Color axis overshoots to 0…25 where DevExpress draws 0…21 (step 3); at 7 both
     land exactly — Color 0…21 step 3, Phosphate 0…1 step 0.2. The category axis keeps 5,
     which gives the right MAX on both (20 and 1); its spacing can still be coarser than
     DevExpress's, because DevExpress fits tick count to the axis's pixel width and that is
     not something this reconstruction can know. */
  const yA = niceAxis(Math.max(0, ...points.map(p => p.abs)), 7);
  const xA = niceAxis(Math.max(0, ...points.map(p => p.conc)));
  return (
    <div style={floating
      ? { width: '100%', height: '100%', padding: '6px 8px', boxSizing: 'border-box', background: '#fff' }
      : { padding: '12px 16px 18px', background: '#fff' }}>
      {!floating && (
        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#333', marginBottom: 4 }}>
          Calibration Curve{r2 ? <span style={{ fontWeight: 400, color: 'var(--ai-muted)', marginLeft: 10 }}>R² = {r2}</span> : null}
        </div>
      )}
      <div style={floating ? { width: '100%', height: '100%' } : { width: 560, maxWidth: '100%', height: 280 }}>
        <ResponsiveContainer>
          <LineChart data={points}
            margin={floating ? { top: 10, right: 14, bottom: 20, left: 4 } : { top: 8, right: 28, bottom: 26, left: 10 }}>
            {/* Horizontal major gridlines at each Absorbance tick (0.2, 0.4, …)
                — Excel calibration-chart parity: ALPACA's embedded chart shows
                these.  Vertical gridlines OFF, matching Excel's default. */}
            {floating
              ? <CartesianGrid horizontal vertical={false} stroke="#d9d9d9" />
              : <CartesianGrid stroke="#e3e9f1" />}
            <XAxis type="number" dataKey="conc"
              domain={floating ? [0, xA.max] : ['dataMin', 'dataMax']}
              ticks={floating ? xA.ticks : undefined}
              allowDataOverflow={floating ? true : undefined}
              fontSize={fs} tick={{ fill: '#555' }}
              label={floating ? undefined : { value: 'Known Conc', position: 'insideBottom', offset: -14, fontSize: 11, fill: '#555' }} />
            {/* Absorbance axis fixed 0 → nice max, 4-decimal labels (LIMS parity).
                allowDataOverflow hard-clamps the axis to [0, max] so the
                blank row's -0.0107 can't push the baseline negative.
                The axis TITLE is BOLD because the template's own chart says so:
                xl/charts/chart1.xml carries <a:defRPr sz="1000" b="1"/> on this
                title, which is why ALPACA renders it bold (client 2026-08-24). */}
            <YAxis domain={floating ? [0, yA.max] : undefined}
              ticks={floating ? yA.ticks : undefined}
              allowDataOverflow={floating ? true : undefined}
              width={floating ? 54 : undefined}
              tickFormatter={floating ? axisFmt : undefined}
              fontSize={fs} tick={{ fill: '#555' }}
              label={{ value: series?.yTitle ?? 'Absorbance', angle: -90, position: 'insideLeft', fontSize: fs, fill: '#555', fontWeight: 700 }} />
            <Tooltip formatter={(v: number) => v.toFixed(4)} labelFormatter={(l) => `Conc: ${l}`} />
            {/* Only when the template's chart carries a <c:legend>. Phosphate has one on
                the right; Color has none, and LIMS draws neither more nor less. */}
            {series?.legend !== false && (
              <Legend
                layout={floating ? 'vertical' : 'horizontal'}
                align={floating ? 'right' : 'center'}
                verticalAlign={floating ? 'middle' : 'bottom'} />
            )}
            {/* Plot-area frame top + right edges (the Y/X axis lines are the
                left + bottom) so the plot is a full box like ALPACA's chart.
                ifOverflow="visible" keeps them from being discarded for sitting
                exactly on the domain boundary. */}
            {floating && <ReferenceLine y={yA.max} stroke="#666" strokeWidth={1} ifOverflow="visible" />}
            {floating && <ReferenceLine x={xA.max} stroke="#666" strokeWidth={1} ifOverflow="visible" />}
            <Line type="linear" dataKey="abs" name="Series 1" stroke="#2f7fd1" strokeWidth={2}
              dot={floating ? (series?.markers ? { r: 3 } : false) : { r: 2 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/** "B12" → { r:12, c:2 } (1-based).  Small local parser so we don't pull in a
 *  cell-ref util just for merges. */
function cellRef(ref: string): { r: number; c: number } | null {
  const m = /^([A-Z]+)(\d+)$/.exec(ref.trim().toUpperCase());
  if (!m) return null;
  const letters = m[1]!;
  let c = 0;
  for (const ch of letters) c = c * 26 + (ch.charCodeAt(0) - 64);
  return { r: parseInt(m[2]!, 10), c };
}

/** 1-based column number → Excel letter (1→A, 27→AA). */
function colLetter(n: number): string {
  let s = '';
  while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = (n - 1 - m) / 26; }
  return s || 'A';
}

/** One row for the SDMS ribbon's Test / QCBatchID pickers.  Derived from the
 *  batch list the SDMS Result Validation page already loaded — no new API. */
export interface SdmsNavBatch {
  id:           string;          // AnalyticalBatch.Oid
  batchId:      string | null;   // QCBatchID
  test:         string | null;
  method:       string | null;
  matrix:       string | null;
  templateId:   number | null;
  templateName: string | null;   // SpreadSheetBuilder template name ("Phosphate")
  jobIds:       string | null;
  statusText:   string;
}

interface SdmsSpreadsheetProps {
  batch:     SdmsBatch;
  onClose:   () => void;
  /** Notify parent when Rollback succeeds (so detail-view rows update). */
  onRolledBack?: () => void;
  /** Fired after the sheet is saved. The Update writes the edited cells to
   *  SpreadSheetEntry_SampleInfo — the SAME table Batch Results reads — so the
   *  grid behind this sheet is stale the moment a save lands. The parent reloads
   *  it rather than making the analyst press Refresh (client 2026-08-19). */
  onSaved?: () => void;
  /** All SDMS batches (for the ribbon Test / QCBatchID pickers).  Optional —
   *  when omitted, the pickers show just the current batch (read-only). */
  allBatches?: SdmsNavBatch[];
  /** Preview mode (LIMS parity): render the bare spreadsheet only — no ribbon
   *  tabs, no ribbon toolbar, no Mode/Test/QCBatchID pickers.  ALPACA's Preview
   *  opens the SDMS sheet read-only with just the grid + sheet tabs. */
  preview?: boolean;
  /** DF values the sheet's DF column is bound to, so it renders what LIMS renders
   *  (see SdmsDfOverlay in SdmsUniverSheet). Passed straight through. */
  dfOverlay?: SdmsDfOverlay;
  /** Start in full-screen (fixed, inset:0). Used when opened from inside the centered
   *  Data Package modal, where the default `absolute; inset:0` would clip to the modal
   *  box. The user can still toggle full-screen off with the ribbon button. */
  startFullscreen?: boolean;
}

// ── Multi-column ribbon dropdown (LIMS parity for Test / QCBatchID) ────────
// A button showing the current value; clicking opens a popup table with the
// given columns/rows.  Row click → onSelect.  Matches ALPACA's grid-style
// SDMS pickers.
interface GridCol { key: string; label: string; width?: number }
interface PickRow { key: string; [k: string]: string }
function GridDropdown({
  valueLabel, columns, rows, selectedKey, onSelect, minWidth = 460,
}: {
  valueLabel: string;
  columns: GridCol[];
  rows: readonly PickRow[];
  selectedKey?: string;
  onSelect: (key: string) => void;
  minWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const btnRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const selRowRef = useRef<HTMLTableRowElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r => columns.some(c => (r[c.key] ?? '').toLowerCase().includes(t)));
  }, [rows, q, columns]);

  // On open, scroll the currently-selected row to the centre of the list —
  // ALPACA opens the Test / QCBatchID picker focused on the current selection,
  // not scrolled to the top.  Uses client rects so it's independent of the
  // row's offsetParent.
  useEffect(() => {
    if (!open) return;
    const cont = scrollRef.current, row = selRowRef.current;
    if (!cont || !row) return;
    const rr = row.getBoundingClientRect(), cr = cont.getBoundingClientRect();
    cont.scrollTop += (rr.top - cr.top) - cont.clientHeight / 2 + rr.height / 2;
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      // Fixed-position popup measured from the button — escapes the ribbon's
      // overflow:auto clip.  Clamp within the viewport's right/bottom edges.
      const left = Math.min(r.left, window.innerWidth - minWidth - 12);
      setPos({ top: r.bottom + 3, left: Math.max(8, left) });
    }
    setOpen(o => !o);
  };

  return (
    <span className="ss-dd">
      <button ref={btnRef} className="ss-dd-btn" onClick={toggle} title={valueLabel}>
        <span className="ss-dd-val">{valueLabel || '—'}</span>
        <i className="ti ti-chevron-down" />
      </button>
      {open && (
        <>
          <div className="ss-dd-backdrop" onClick={() => { setOpen(false); setQ(''); }} />
          <div className="ss-dd-pop" style={{ width: minWidth, top: pos.top, left: pos.left }}>
            <div className="ss-dd-search">
              <i className="ti ti-search" />
              <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" />
            </div>
            <div className="ss-dd-scroll" ref={scrollRef}>
              <table className="ss-dd-tbl">
                <thead>
                  <tr>{columns.map(c => <th key={c.key} style={{ width: c.width }}>{c.label}</th>)}</tr>
                </thead>
                <tbody>
                  {shown.map(r => (
                    <tr key={r['_rk'] ?? r.key}
                      ref={r.key === selectedKey ? selRowRef : undefined}
                      className={r.key === selectedKey ? 'ss-dd-sel' : ''}
                      onClick={() => { onSelect(r.key); setOpen(false); setQ(''); }}>
                      {columns.map(c => <td key={c.key}>{r[c.key] ?? ''}</td>)}
                    </tr>
                  ))}
                  {shown.length === 0 && (
                    <tr><td colSpan={columns.length} className="ss-dd-empty">No matches.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//   Per-test presets — columns + header rows + data seed
// ─────────────────────────────────────────────────────────────────────────

type CellKind = 'hdr' | 'lbl' | 'yel' | 'title' | 'data';
interface Cell {
  v:      string;
  k?:     CellKind;
  align?: 'right';
  green?: boolean;    // within-limits QC marker (LOQ/LCS/CCV recoveries) + QCStatus=Pass + IsReport=True
  red?:   boolean;    // QCStatus=Fail + IsReport=False
  span?:  number;     // colspan
}

interface SdmsPreset {
  /** Excel column letters this preset uses (A..R for Alkalinity, A..O for IC). */
  cols:        readonly string[];
  /** Display label for each column header (row 7). */
  hdrLabels:   Readonly<Record<string, string>>;
  /** Columns that render right-aligned by default. */
  rightCols:   ReadonlySet<string>;
  /** Build the test-specific header rows (1..6).  `batch` lets the row include
   *  live values (Test, Method, QCBatchID, etc.). */
  headerRows:  (batch: SdmsBatch) => Array<{ n: number; cells: Record<string, Cell> }>;
  /** Representative result rows for Sheet1.  Each row is a tuple aligned with
   *  `cols`.  The 0th column is the "SysSampleCode" — used by the QC-green
   *  highlight rule below. */
  sheet1Rows:  readonly string[][];
  /** Columns whose cells should be highlighted green when the row is a
   *  within-limits QC (LOQ / LCS / CCV / Rec %).  Per-preset because each
   *  test surfaces the recovery in a different column. */
  qcGreenCols: ReadonlySet<string>;
}

// ── Alkalinity preset (matches legacy SDMS screenshot 2026-06-30) ───────
// Full 27-column SDMS layout (A..AA): the 18 column-headers + 9 trailing
// QC/audit columns the legacy spreadsheet shows after the QC limits — the
// per-sample Qualifier / QCStatus / Dup-Source / Spike-Source / IsReport
// / AnalysisDate / Analyst trail.
const ALKALINITY_PRESET: SdmsPreset = {
  cols: [
    'A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R',
    'S','T','U','V','W','X','Y','Z','AA',
  ],
  hdrLabels: {
    A:  'SysSampleCode',      B:  'SampleType',          C:  'SiteName',
    D:  'Parameter',          E:  'Sample Volume (mL)',  F:  'Numeric Result',
    G:  'Result',             H:  'Units',               I:  'DF',
    J:  'RL',                 K:  'SpikeAmount',         L:  'MDL',
    M:  'UQL',                N:  'MCL',                 O:  'RPD %',
    P:  'Rec %',              Q:  'RPD CtrlLimit(%)',    R:  'Rec CtrlLimit(%)',
    S:  'Qualifier',          T:  'QCStatus',            U:  'DupSouce',
    V:  'DupSouce Result',    W:  'SpikeSouce',          X:  'SpikeSouce Resut',
    Y:  'IsReport',           Z:  'AnalysisDate',        AA: 'Analyst',
  },
  rightCols: new Set(['E','F','G','I','J','K','L','M','N','O','P','V','X']),
  headerRows: (b) => {
    const test    = b.test    ?? '';
    const method  = b.method  ?? '';
    const qcBatch = b.batchId ?? '';
    const rows: Array<{ n: number; cells: Record<string, Cell> }> = [
      // Big title cell, top-right — spans the last two columns of the table.
      { n: 1, cells: { N: { v: `${test || 'Alkalinity'}-Mantech`, k: 'title', span: 14 } } },
      { n: 2, cells: {
        A: { v: 'Test',                       k: 'lbl' },
        B: { v: test },
        C: { v: 'Spike Conc. (mg/L)',         k: 'lbl' },
        D: { v: '5000',                       k: 'yel', align: 'right' },
        E: { v: 'CCV Low of STD Used (mg/L)', k: 'lbl', span: 2 },
        G: { v: '2.5',                        k: 'yel', align: 'right' },
        H: { v: 'CCV Low Value (mg/L)',       k: 'lbl', span: 2 },
        J: { v: '50',                         k: 'yel', align: 'right' },
      } },
      { n: 3, cells: {
        A: { v: 'Method',                      k: 'lbl' },
        B: { v: method },
        C: { v: 'Spike Volume (mL)',           k: 'lbl' },
        D: { v: '0.50',                        k: 'yel', align: 'right' },
        E: { v: 'CCV High of STD Used (mg/L)', k: 'lbl', span: 2 },
        G: { v: '5.0',                         k: 'yel', align: 'right' },
        H: { v: 'CCV High Value (mg/L)',       k: 'lbl', span: 2 },
        J: { v: '100',                         k: 'yel', align: 'right' },
      } },
      { n: 4, cells: {
        A: { v: 'Instrument',           k: 'lbl' },
        B: { v: 'ManTech Automax-' },
        C: { v: 'Pipette ID',           k: 'lbl' },
        D: { v: '3307581',              k: 'yel', align: 'right' },
        H: { v: 'QCBatchID',            k: 'lbl', span: 2 },
        J: { v: qcBatch },
      } },
      { n: 5, cells: {} },
      { n: 6, cells: { A: { v: 'Remarks', k: 'lbl' }, B: { v: '', k: 'yel', span: 7 } } },
    ];
    return rows;
  },
  // Column order (27 cells per row):
  //  A SysSampleCode · B SampleType · C SiteName · D Parameter · E SampleVol ·
  //  F NumericResult · G Result · H Units · I DF · J RL · K SpikeAmount ·
  //  L MDL · M UQL · N MCL · O RPD % · P Rec % · Q RPDCtrlLimit · R RecCtrlLimit ·
  //  S Qualifier · T QCStatus · U DupSouce · V DupSouce Result ·
  //  W SpikeSouce · X SpikeSouce Resut · Y IsReport · Z AnalysisDate · AA Analyst
  sheet1Rows: [
    ['Method Blank1',     'Method Blank', '',         'Alkalinity', '50.00', '1.48',   '<20',  'mg/L', '1', '20', '',    '2.14', '', '',     '',    '',      '',     '',         '', 'Pass', '',              '',     '',              '',     'True', '05/29/2026 10:38', 'E169217'],
    ['CCV Low1',          'CCV Low',      '',         'Alkalinity', '50.00', '52.70',  '53',   'mg/L', '1', '20', '50',  '2.14', '', '',     '',    '105.4', '',     '90 - 110', '', 'Pass', '',              '',     '',              '',     'True', '05/29/2026 10:45', 'E169217'],
    ['CCV High1',         'CCV High',     '',         'Alkalinity', '50.00', '100.30', '100',  'mg/L', '1', '20', '100', '2.14', '', '',     '',    '100.3', '',     '90 - 110', '', 'Pass', '',              '',     '',              '',     'True', '05/29/2026 10:54', 'E169217'],
    ['LCS1',              'LCS',          '',         'Alkalinity', '50.00', '53.10',  '53',   'mg/L', '1', '20', '50',  '2.14', '', '',     '',    '106.2', '',     '80 - 120', '', 'Pass', '',              '',     '',              '',     'True', '05/29/2026 11:05', 'E169217'],
    ['260529002-002',     'Sample',       'D1',       'Alkalinity', '50.00', '86.70',  '87',   'mg/L', '1', '20', '',    '2.14', '', '2000', '',    '',      '',     '',         '', '',     '',              '',     '',              '',     'True', '05/29/2026 11:14', 'E169217'],
    ['260529002-002Dup1', 'Dup',          'D1',       'Alkalinity', '50.00', '85.70',  '86',   'mg/L', '1', '20', '',    '2.14', '', '2000', '1.2', '',      '20',   '',         '', 'Pass', '260529002-002', '86.7', '',              '',     'True', '05/29/2026 11:24', 'E169217'],
    ['260529002-002MS1',  'MS',           'D1',       'Alkalinity', '50.00', '132.10', '132',  'mg/L', '1', '20', '50',  '2.14', '', '2000', '',    '90.8',  '',     '80 - 120', '', 'Pass', '',              '',     '260529002-002', '86.7', 'True', '05/29/2026 11:35', 'E169217'],
    ['260529001-001',     'Sample',       'R4',       'Alkalinity', '50.00', '84.30',  '84',   'mg/L', '1', '20', '',    '2.14', '', '2000', '',    '',      '',     '',         '', '',     '',              '',     '',              '',     'True', '05/29/2026 11:49', 'E169217'],
    ['260529001-002',     'Sample',       'D4',       'Alkalinity', '50.00', '87.90',  '88',   'mg/L', '1', '20', '',    '2.14', '', '2000', '',    '',      '',     '',         '', '',     '',              '',     '',              '',     'True', '05/29/2026 11:59', 'E169217'],
    ['260529002-001',     'Sample',       'R1',       'Alkalinity', '50.00', '85.20',  '85',   'mg/L', '1', '20', '',    '2.14', '', '2000', '',    '',      '',     '',         '', '',     '',              '',     '',              '',     'True', '05/29/2026 12:09', 'E169217'],
    ['260529002-003',     'Sample',       'R3',       'Alkalinity', '50.00', '85.50',  '86',   'mg/L', '1', '20', '',    '2.14', '', '2000', '',    '',      '',     '',         '', '',     '',              '',     '',              '',     'True', '05/29/2026 12:20', 'E169217'],
    ['260529002-004',     'Sample',       'D3',       'Alkalinity', '50.00', '88.30',  '88',   'mg/L', '1', '20', '',    '2.14', '', '2000', '',    '',      '',     '',         '', '',     '',              '',     '',              '',     'True', '05/29/2026 12:31', 'E169217'],
    ['260529003-001',     'Sample',       'P2 NER',   'Alkalinity', '50.00', '62.30',  '62',   'mg/L', '1', '20', '',    '2.14', '', '2000', '',    '',      '',     '',         '', '',     '',              '',     '',              '',     'True', '05/29/2026 12:41', 'E169217'],
    ['260529003-002',     'Sample',       'P2Ph1NED1','Alkalinity', '50.00', '73.70',  '74',   'mg/L', '1', '20', '',    '2.14', '', '2000', '',    '',      '',     '',         '', '',     '',              '',     '',              '',     'True', '05/29/2026 12:50', 'E169217'],
    ['260529003-006',     'Sample',       'P2Ph2CS3', 'Alkalinity', '50.00', '53.30',  '53',   'mg/L', '1', '20', '',    '2.14', '', '2000', '',    '',      '',     '',         '', '',     '',              '',     '',              '',     'True', '05/29/2026 13:21', 'E169217'],
    ['260529003-005',     'Sample',       'P2Ph1CS2', 'Alkalinity', '50.00', '53.90',  '54',   'mg/L', '1', '20', '',    '2.14', '', '2000', '',    '',      '',     '',         '', '',     '',              '',     '',              '',     'True', '05/29/2026 13:30', 'E169217'],
    ['CCV Low2',          'CCV Low',      '',         'Alkalinity', '50.00', '51.30',  '51',   'mg/L', '1', '20', '50',  '2.14', '', '',     '',    '102.6', '',     '90 - 110', '', 'Pass', '',              '',     '',              '',     'True', '05/29/2026 13:40', 'E169217'],
    ['CCB1',              'CCB',          '',         'Alkalinity', '50.00', '1.15',   '<20',  'mg/L', '1', '20', '',    '2.14', '', '',     '',    '',      '',     '',         '', 'Pass', '',              '',     '',              '',     'True', '05/29/2026 13:55', 'E169217'],
    ['260529003-009',     'Sample',       'P2Ph1Rmx2','Alkalinity', '50.00', '56.10',  '56',   'mg/L', '1', '20', '',    '2.14', '', '2000', '',    '',      '',     '',         '', '',     '',              '',     '',              '',     'True', '05/29/2026 14:01', 'E169217'],
    ['260529003-010',     'Sample',       'P2Ph2Rmx3','Alkalinity', '50.00', '55.70',  '56',   'mg/L', '1', '20', '',    '2.14', '', '2000', '',    '',      '',     '',         '', '',     '',              '',     '',              '',     'True', '05/29/2026 14:10', 'E169217'],
    ['CCV Low3',          'CCV Low',      '',         'Alkalinity', '50.00', '52.10',  '52',   'mg/L', '1', '20', '50',  '2.14', '', '',     '',    '104.2', '',     '90 - 110', '', 'Pass', '',              '',     '',              '',     'True', '05/29/2026 14:20', 'E169217'],
    ['CCB2',              'CCB',          '',         'Alkalinity', '50.00', '0.39',   '<20',  'mg/L', '1', '20', '',    '2.14', '', '',     '',    '',      '',     '',         '', 'Pass', '',              '',     '',              '',     'True', '05/29/2026 14:29', 'E169217'],
  ],
  qcGreenCols: new Set(['O','P']),     // Rec % + RPD % when within limits
};

// ── Calcium Titration preset (matches ALPACA LIMS screenshot 2026-07-01) ─
// Legacy SM 3500-CA B "Calcium Titration" SDMS layout — 20 columns, EDTAVolume
// as the workflow-specific measurement, and a Calcium-Titration title strip
// with Amount of Spiking Solution / STD 20 ppm / 40 ppm / Burette Cal Date /
// Spiking Solution Concentration / EDTA Open Date / Pipet used for Spiking
// stamps in the top rows.  Data columns mirror the ALPACA screenshot
// verbatim (QCType instead of SampleType, mg Ca/L units, Rec % gate 90-110).
const CALCIUM_PRESET: SdmsPreset = {
  cols: [
    'A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T',
  ],
  hdrLabels: {
    A: 'SysSampleCode',        B: 'QCType',              C: 'SiteName',
    D: 'Sample Volume (ml)',   E: 'EDTAVolume (ml)',     F: 'Numeric Result (mg Ca/L)',
    G: 'Result (mg Ca/L)',     H: 'Units',               I: 'DF',
    J: 'RL',                   K: 'Spike Amount',        L: 'RPD %',
    M: 'Rec %',                N: 'RPD CtrlLimit(%)',    O: 'Rec CtrlLimit(%)',
    P: 'QCSample Results',     Q: 'Qualifier',           R: 'QCStatus',
    S: 'AnalysisDate',         T: 'Analyst',
  },
  rightCols: new Set(['D','E','F','G','I','J','K','L','M']),
  headerRows: (b) => {
    const test    = b.test    ?? '';
    const method  = b.method  ?? '';
    const matrix  = 'Water';
    const qcBatch = b.batchId ?? '';
    const rows: Array<{ n: number; cells: Record<string, Cell> }> = [
      // Top title spanning the middle columns (matches LIMS "Calcium Titration" strip).
      { n: 1, cells: { E: { v: `${test || 'Calcium'} Titration`, k: 'title', span: 10 } } },
      { n: 2, cells: {
        A: { v: 'Test',                                  k: 'lbl' },
        B: { v: test || 'Calcium' },
        C: { v: 'Amount of Spiking Solution added (ul)', k: 'lbl', span: 2 },
        E: { v: '50',                                    k: 'yel', align: 'right' },
        F: { v: 'STD 20ppm Value CCV Low',               k: 'lbl', span: 2 },
        H: { v: '20',                                    k: 'yel', align: 'right' },
        I: { v: 'Burette Cal Date',                      k: 'lbl', span: 2 },
        K: { v: '01/15/2026',                            k: 'yel' },
      } },
      { n: 3, cells: {
        A: { v: 'Method',                                k: 'lbl' },
        B: { v: method || 'SM 3500-CA B' },
        C: { v: 'Spiking Solution Concentration (ppm)',  k: 'lbl', span: 2 },
        E: { v: '50,000',                                k: 'yel', align: 'right' },
        F: { v: 'STD 40ppm Value CCV High',              k: 'lbl', span: 2 },
        H: { v: '40',                                    k: 'yel', align: 'right' },
        I: { v: 'QCBatchID',                             k: 'lbl', span: 2 },
        K: { v: qcBatch },
      } },
      { n: 4, cells: {
        A: { v: 'Matrix',                                k: 'lbl' },
        B: { v: matrix },
        C: { v: 'EDTA Open Date',                        k: 'lbl', span: 2 },
        E: { v: '03/23/2026',                            k: 'yel' },
        F: { v: 'Pipet used for Spiking',                k: 'lbl', span: 2 },
        H: { v: '1246557',                               k: 'yel', align: 'right' },
      } },
      { n: 5, cells: { A: { v: 'Remarks', k: 'lbl' } } },
      { n: 6, cells: {} },
    ];
    return rows;
  },
  sheet1Rows: [
    ['Method Blank1', 'Method Blank', '',       '50', '0.00',  '0.00',  '<0.5',  'mg/L', '1', '0.5', '',   '', '',     '',         '',         '', '', 'Pass', '04/02/2026 12:14', 'E129500'],
    ['CCV Low1',      'CCV Low',      '',       '50', '2.35',  '18.84', '',      'mg/L', '1', '1.0', '20', '', '94.2', '',         '90 - 110', '', '', 'Pass', '04/02/2026 12:14', 'E129500'],
    ['CCV High1',     'CCV High',     '',       '50', '4.68',  '37.51', '37.51', 'mg/L', '1', '1.0', '40', '', '93.8', '',         '90 - 110', '', '', 'Pass', '04/02/2026 12:14', 'E129500'],
    ['LCS1',          'LCS',          '',       '50', '2.45',  '19.64', '19.64', 'mg/L', '1', '1.0', '20', '', '98.2', '',         '90 - 110', '', '', 'Pass', '04/02/2026 12:14', 'E129500'],
    ['260402001-001', 'Sample',       'R4',     '50', '5.35',  '42.89', '42.89', 'mg/L', '1', '1.0', '',   '', '',     '',         '',         '', '', '',     '04/02/2026 12:14', 'E129500'],
    ['260402001-002', 'Sample',       'D4',     '50', '5.88',  '47.13', '47.13', 'mg/L', '1', '1.0', '',   '', '',     '',         '',         '', '', '',     '04/02/2026 12:14', 'E129500'],
    ['260402002-001', 'Sample',       'R1',     '50', '5.25',  '42.08', '42.08', 'mg/L', '1', '1.0', '',   '', '',     '',         '',         '', '', '',     '04/02/2026 12:14', 'E129500'],
    ['260402002-002', 'Sample',       'D1',     '50', '5.21',  '41.76', '41.76', 'mg/L', '1', '1.0', '',   '', '',     '',         '',         '', '', '',     '04/02/2026 12:14', 'E129500'],
    ['260402002-003', 'Sample',       'R3',     '50', '5.38',  '43.13', '43.13', 'mg/L', '1', '1.0', '',   '', '',     '',         '',         '', '', '',     '04/02/2026 12:14', 'E129500'],
    ['260402002-004', 'Sample',       'D3',     '50', '5.31',  '42.56', '42.56', 'mg/L', '1', '1.0', '',   '', '',     '',         '',         '', '', '',     '04/02/2026 12:14', 'E129500'],
    ['260402002-004Dup1','Dup',       'D3',     '50', '5.30',  '42.48', '42.48', 'mg/L', '1', '1.0', '',   '0.2', '',   '20',       '',         '42.56', '', 'Pass', '04/02/2026 12:14', 'E129500'],
    ['260402002-004MS1', 'MS',        'D3',     '50', '7.71',  '61.80', '61.80', 'mg/L', '1', '1.0', '20', '', '96.2', '',         '80 - 120', '42.56', '', 'Pass', '04/02/2026 12:14', 'E129500'],
    ['260402003-001', 'Sample',       'P2 NER', '50', '2.92',  '23.41', '23.41', 'mg/L', '1', '1.0', '',   '', '',     '',         '',         '', '', '',     '04/02/2026 12:14', 'E129500'],
    ['260402003-002', 'Sample',       'P2Ph1NED1','50','2.78', '22.28', '22.28', 'mg/L', '1', '1.0', '',   '', '',     '',         '',         '', '', '',     '04/02/2026 12:14', 'E129500'],
    ['260402003-009', 'Sample',       'P2Ph1Rmx2','50','2.56', '20.52', '20.52', 'mg/L', '1', '1.0', '',   '', '',     '',         '',         '', '', '',     '04/02/2026 12:14', 'E129500'],
    ['260402003-010', 'Sample',       'P2Ph2Rmx3','50','2.51', '20.12', '20.12', 'mg/L', '1', '1.0', '',   '', '',     '',         '',         '', '', '',     '04/02/2026 12:14', 'E129500'],
    ['CCV Low2',      'CCV Low',      '',       '50', '2.37',  '19.00', '19.00', 'mg/L', '1', '1.0', '20', '', '95.0', '',         '90 - 110', '', '', 'Pass', '04/02/2026 12:14', 'E129500'],
    ['CCB1',          'CCB',          '',       '50', '0.00',  '0.00',  '<0.5',  'mg/L', '1', '0.5', '',   '', '',     '',         '',         '', '', 'Pass', '04/02/2026 12:14', 'E129500'],
  ],
  qcGreenCols: new Set(['L','M']),    // RPD % + Rec % green when within limits
};

// ── Anions/IC preset (the prototype's original Sheet1 — 15 cols) ────────
const ANIONS_PRESET: SdmsPreset = {
  cols: ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O'],
  hdrLabels: {
    A: 'SysSampleCode', B: 'SampleType',     C: 'SiteName',
    D: 'Parameter',     E: 'SampleFiltered', F: 'NumericResult (mg/L)',
    G: 'Result (mg/L)', H: 'Units',          I: 'DF',
    J: 'RL',            K: 'MDL',            L: 'UQL',
    M: 'MCL',           N: 'Spike Amount (mg/L)', O: 'RPD %',
  },
  rightCols: new Set(['F','G','I','J','K','L','M','N']),
  headerRows: (b) => {
    const test    = b.test    ?? '';
    const method  = b.method  ?? '';
    const qcBatch = b.batchId ?? '';
    const rows: Array<{ n: number; cells: Record<string, Cell> }> = [
      { n: 1, cells: { N: { v: test, k: 'title', span: 2 } } },
      { n: 2, cells: {
        A: { v: 'Test',                       k: 'lbl' },
        B: { v: test },
        C: { v: 'Sequence ID',                k: 'lbl' },
        D: { v: '062226A_Daily',              k: 'yel' },
        E: { v: 'Eluent Exp Date',            k: 'lbl' },
        F: { v: '12/18/2027',                 k: 'yel' },
        G: { v: 'Last Cal Date',              k: 'lbl' },
        H: { v: '06/02/2026',                 k: 'yel' },
      } },
      { n: 3, cells: {
        A: { v: 'Method',                     k: 'lbl' },
        B: { v: method },
        C: { v: 'Sample Volume taken mL',     k: 'lbl' },
        D: { v: '4.9',                        k: 'yel', align: 'right' },
        E: { v: 'Filter Lot',                 k: 'lbl' },
        F: { v: '12K961',                     k: 'yel' },
        G: { v: 'QC Batch ID',                k: 'lbl' },
        H: { v: qcBatch },
      } },
      { n: 4, cells: {
        A: { v: 'Matrix',                     k: 'lbl' },
        B: { v: 'Water' },
        C: { v: 'Spiking Solution added mL',  k: 'lbl' },
        D: { v: '0.1',                        k: 'yel', align: 'right' },
        E: { v: 'Pump Pressure',              k: 'lbl' },
        F: { v: '3147 psi',                   k: 'yel' },
      } },
      { n: 5, cells: {
        A: { v: 'Instrument ID',              k: 'lbl' },
        B: { v: 'Thermal IC-6000 D' },
        C: { v: 'Column Used',                k: 'lbl' },
        D: { v: 'AS19_4um',                   k: 'yel' },
        E: { v: 'Pipette ID',                 k: 'lbl' },
        F: { v: 'R21528F/541451403',          k: 'yel' },
      } },
      { n: 6, cells: { A: { v: 'Remarks', k: 'lbl' }, B: { v: '', k: 'yel', span: 7 } } },
    ];
    return rows;
  },
  sheet1Rows: [
    ['CCB1','CCB','','Chloride',                  'No','n.a.',  '<1',     'mg/L','1','1.0','0.176','100','',   ''],
    ['CCB1','CCB','','Nitrite as Nitrogen',       'No','n.a.',  '<0.04',  'mg/L','1','0.0','0.003','5',  '',   ''],
    ['CCB1','CCB','','Bromide',                   'No','n.a.',  '<0.02',  'mg/L','1','0.0','0.005','5',  '',   ''],
    ['CCB1','CCB','','Nitrate as Nitrogen',       'No','n.a.',  '<0.04',  'mg/L','1','0.0','0.007','5',  '',   ''],
    ['CCB1','CCB','','Sulfate',                   'No','n.a.',  '<1',     'mg/L','1','1.0','0.337','5',  '',   ''],
    ['CCB1','CCB','','O-phosphate as Phosphorus', 'No','n.a.',  '<0.1',   'mg/L','1','0.1','0.019','5',  '',   ''],
    ['LOQ1','LOQ','','Chloride',                  'No','0.9797','0.9797', 'mg/L','1','',   '0.176','100','1',  ''],
    ['LOQ1','LOQ','','Nitrite as Nitrogen',       'No','0.0386','0.0386', 'mg/L','1','',   '0.003','5',  '0.04',''],
    ['LOQ1','LOQ','','Bromide',                   'No','0.0206','0.02',   'mg/L','1','',   '0.005','5',  '0.02',''],
    ['LOQ1','LOQ','','Nitrate as Nitrogen',       'No','0.0441','0.0441', 'mg/L','1','',   '0.007','5',  '0.04',''],
    ['LOQ1','LOQ','','Sulfate',                   'No','0.7615','1',      'mg/L','1','',   '0.337','5',  '1',  ''],
    ['LOQ1','LOQ','','O-phosphate as Phosphorus', 'No','0.1076','0.11',   'mg/L','1','',   '0.019','5',  '0.1',''],
    ['LCS1','LCS','','Chloride',                  'No','50.2681','50.2681','mg/L','1','',  '0.176','100','50', ''],
    ['LCS1','LCS','','Nitrite as Nitrogen',       'No','2.4222','2.4222', 'mg/L','1','',   '0.003','5',  '2.5',''],
  ],
  qcGreenCols: new Set(['F','G']),
};

/** Pick a per-test preset.  Match is case-insensitive substring; default
 *  falls back to the Anions/IC layout (the prototype's original).  This is
 *  a temporary scaffold — Phase 5 hydrates the SAME table shape from a
 *  per-batch SP that owns the column set + header values per test.
 *
 *  Test → preset routing:
 *    - Alkalinity              → Alkalinity-Mantech titration layout (27 cols)
 *    - Calcium / Total Hardness / Magnesium → SM 3500-CA B Titration layout (20 cols)
 *    - everything else         → Anions/IC layout (15 cols, prototype default)
 *
 *  Total Hardness and Magnesium are titration workflows that share the same
 *  EDTAVolume-based sheet as Calcium in legacy ALPACA (see the SM 234x /
 *  3500 method family), so we route them all to CALCIUM_PRESET. */
function presetFor(testName: string | null): SdmsPreset {
  const t = (testName ?? '').toLowerCase();
  if (t.includes('alkalinity')) return ALKALINITY_PRESET;
  if (t.includes('calcium') || t.includes('hardness') || t.includes('magnesium')) return CALCIUM_PRESET;
  return ANIONS_PRESET;
}

/** Build the full Sheet1 model — header rows then column-header row then
 *  data rows then a handful of empty rows so the sheet looks like a sheet. */
function buildSheet1Model(batch: SdmsBatch, preset: SdmsPreset): Array<{ n: number; cells: Record<string, Cell> }> {
  const model: Array<{ n: number; cells: Record<string, Cell> }> = preset.headerRows(batch);

  // Column-header row sits AFTER the test-specific header rows (legacy = row 7).
  const headerRowN = model.length + 1;
  const hdr: Record<string, Cell> = {};
  preset.cols.forEach((c) => { hdr[c] = { v: preset.hdrLabels[c] ?? '', k: 'hdr' }; });
  model.push({ n: headerRowN, cells: hdr });

  preset.sheet1Rows.forEach((d, idx) => {
    const cells: Record<string, Cell> = {};
    const sampleType = d[1] ?? '';
    // Row's "within-limits QC" classification — any LOQ/LCS/CCV/Recovery row
    // surfaces its numeric columns in green to flag "passes".  Real
    // pass/fail comes from the legacy %Rec ranges per test method in Phase 5.
    const isQC = /^(LOQ|LCS|CCV|MS|Dup)/i.test(d[0] ?? '')
             || /^(LCS|MS|CCV|Dup|LOQ)/i.test(sampleType);
    preset.cols.forEach((c, i) => {
      const v = d[i] ?? '';
      // Per-value overrides: QCStatus Pass/Fail → green/red; IsReport True/False → green/red.
      const passCell  = preset.hdrLabels[c] === 'QCStatus' && v === 'Pass';
      const failCell  = preset.hdrLabels[c] === 'QCStatus' && v === 'Fail';
      const trueCell  = preset.hdrLabels[c] === 'IsReport'  && v === 'True';
      const falseCell = preset.hdrLabels[c] === 'IsReport'  && v === 'False';
      cells[c] = {
        v, k: 'data',
        align: preset.rightCols.has(c) ? 'right' : undefined,
        green: (isQC && preset.qcGreenCols.has(c) && v !== '') || passCell || trueCell,
        red:   failCell || falseCell,
      };
    });
    model.push({ n: headerRowN + 1 + idx, cells });
  });

  const last = headerRowN + 1 + preset.sheet1Rows.length;
  for (let r = last; r < last + 9; r++) model.push({ n: r, cells: {} });
  return model;
}

// ── ribbon button definitions (button set + order verbatim from ALPACA's
//    SDMS ribbon: Retrieve … Full Screen).  [label, ti-icon, actionId]
//    actionId: 'close' | 'fs' | 'rollback' | 'notes' | 'audit' | 'stub'
type RibbonBtn = readonly [label: string, icon: string, action: string];
const SS_GROUPS: readonly RibbonBtn[][] = [
  [
    ['Retrieve',          'ti-search',          'stub'],
    ['Submit',            'ti-upload',          'stub'],
    ['Save',              'ti-device-floppy',   'stub'],
  ],
  [
    ['New Calibration',   'ti-file-plus',       'stub'],
    ['Attach Calibration','ti-paperclip',       'stub'],
    ['QC Batch',          'ti-table',           'stub'],
    ['QC Batch Results',  'ti-list-check',      'stub'],
  ],
  [
    ['RollBack',          'ti-arrow-back-up',   'rollback'],
    ['Delete',            'ti-x',               'stub'],
    ['Data Package',      'ti-clipboard',       'stub'],
    ['Clear',             'ti-eraser',          'stub'],
    ['Edit',              'ti-file-pencil',     'startEdit'],
    ['BindGrid',          'ti-link',            'stub'],
  ],
  [
    ['Recalculate',       'ti-calculator',      'stub'],
    ['Import Files',      'ti-file-import',     'stub'],
    ['Apply Rounding',    'ti-hash',            'stub'],
  ],
  [
    ['Report',            'ti-file-text',       'stub'],
    ['Project Notes',     'ti-notes',           'notes'],
    ['Qualifiers',        'ti-check',           'stub'],
    ['Audit Trail',       'ti-history',         'audit'],
    ['Full Screen',       'ti-arrows-maximize', 'fs'],
  ],
];

// Per-mode ENABLED state, ported from ALPACA SpreadsheetEntryController.modechange().
// The Mode combo has exactly two items — "View" (default for an exported/submitted
// batch) and "Enter" — so we model both.  In VIEW the sheet is read-only: only
// navigation / rollback / edit-toggle / report / notes / audit act; the data-entry
// buttons (Submit, Save, BindGrid, Recalculate, Import Files, Apply Rounding,
// Qualifiers, New/Attach Calibration, Delete, Clear) are disabled.  In ENTER those
// data-entry buttons enable and RollBack / Edit / Audit Trail disable.
// View-mode (spec 2026-07-02): ONLY Retrieve, RollBack, Edit, Project Notes,
// Audit Trail and Full Screen are enabled — every other button is disabled.
// RollBack is further gated to a VALIDATED batch in ribbonEnabled() (a pending
// batch has nothing to revert).  Enter mode enables the data-entry buttons.
const RIBBON_ENABLE: Record<string, { View: boolean; Enter: boolean }> = {
  'Retrieve':           { View: true,  Enter: true  },
  'Submit':             { View: false, Enter: true  },
  'Save':               { View: false, Enter: true  },
  'New Calibration':    { View: false, Enter: true  },
  'Attach Calibration': { View: false, Enter: true  },
  'QC Batch':           { View: false, Enter: true  },
  'QC Batch Results':   { View: false, Enter: true  },
  'RollBack':           { View: false, Enter: false },   // View gated by isValidated
  'Delete':             { View: false, Enter: true  },
  'Data Package':       { View: false, Enter: true  },
  'Clear':              { View: false, Enter: true  },
  'Edit':               { View: true,  Enter: false },
  'BindGrid':           { View: false, Enter: false },
  'Recalculate':        { View: false, Enter: false },
  'Import Files':       { View: false, Enter: true  },
  'Apply Rounding':     { View: false, Enter: true  },
  'Report':             { View: false, Enter: true  },
  'Project Notes':      { View: true,  Enter: true  },
  'Qualifiers':         { View: false, Enter: true  },
  'Audit Trail':        { View: true,  Enter: false },
  'Full Screen':        { View: true,  Enter: true  },
};

const RIBBON_TABS = ['SDMS','File','Home','Insert','PageLayout','Formulas','Data','Review','View'] as const;
const SHEET_TABS  = ['Sheet1','Reagents','Instruments','Raw Data'] as const;

// ────────────────────────────────────────────────────────────────────────
export function SdmsSpreadsheet({ batch, onClose, onRolledBack, onSaved, allBatches, preview, dfOverlay, startFullscreen }: SdmsSpreadsheetProps) {
  const [fullscreen, setFullscreen] = useState(!!startFullscreen);
  const [mode, setMode]             = useState<'View' | 'Enter'>('View');
  // View sub-mode: clicking Edit unlocks the sheet (ALPACA Isedit=true) and
  // swaps the Clear/Edit ribbon buttons for Cancel/Update.
  const [isEdit, setIsEdit]         = useState(false);
  // Renderer: Univer (editable spreadsheet, migration target) vs the legacy
  // read-only ExcelJS HTML table (kept as an automatic fallback).
  const [useUniver] = useState(true);
  // The sheet currently shown in Univer (reported by SdmsUniverSheet) + the
  // parsed Calibration sheet, so we can overlay the reconstructed calibration
  // chart on the Calibration tab (Univer drops the embedded Excel chart).
  const [univerActiveSheet, setUniverActiveSheet] = useState('');
  const [calGrid, setCalGrid] = useState<LiveGrid | null>(null);
  // What the template chart plots, read from the chart part (see sdmsChartAnchor.ts).
  const [chartSeries, setChartSeries] = useState<Map<string, ChartSeries> | null>(null);
  // Per-sheet embedded-chart box reported by SdmsUniverSheet (see sdmsChartAnchor.ts).
  const [chartBoxes, setChartBoxes] = useState<Record<string, AnchorBox>>({});
  // Handle to the Univer sheet (Update reads the edited .xlsx from it) + a
  // remount key (Cancel bumps it to reload the original, discarding edits).
  const univerRef = useRef<SdmsUniverHandle>(null);
  const [univerKey, setUniverKey] = useState(0);
  const [ribbonTab, setRibbonTab]   = useState<string>('SDMS');
  const [sheetTab, setSheetTab]     = useState<string>('Sheet1');

  // Ribbon Test / QCBatchID navigation.  The pickers are built from the full
  // batch list (allBatches); selecting a QC batch switches which workbook this
  // popup shows (re-fetch keyed on curBatchId).  curTest filters the QCBatchID
  // list to one test, mirroring ALPACA.
  const navBatches = useMemo<SdmsNavBatch[]>(() => allBatches ?? [], [allBatches]);
  const [curBatchId, setCurBatchId] = useState<string>(batch.id);
  const [curTest, setCurTest]       = useState<string>(batch.test ?? '');
  useEffect(() => { setCurBatchId(batch.id); setCurTest(batch.test ?? ''); }, [batch.id, batch.test]);

  // Per-test "Sx" count for the Test picker — the exact ALPACA rule (DISTINCT
  // SampleLogIn per test that are signed-off + instrument-transferred, no result
  // yet, and NOT yet placed in a QC batch).  Fetched once from the API (heavy
  // DISTINCT count, cached server-side); until it loads Sx falls back to the
  // batch count.
  const [sxCounts, setSxCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    let alive = true;
    api.get('/api/v1/analysis/sdms-test-sample-counts')
      .then(res => {
        if (!alive) return;
        const data = (res.data?.data ?? []) as Array<{ test: string | null; count: number }>;
        const m: Record<string, number> = {};
        for (const r of data) if (r.test) m[r.test] = r.count;
        setSxCounts(m);
      })
      .catch(() => { /* Sx falls back to the batch count */ });
    return () => { alive = false; };
  }, []);
  // The batch currently shown — from the nav list if present, else the prop.
  const curBatch = useMemo<SdmsNavBatch>(() => {
    return navBatches.find(b => b.id === curBatchId) ?? {
      id: batch.id, batchId: batch.batchId, test: batch.test, method: batch.method,
      matrix: null, templateId: batch.templateID, templateName: null,
      jobIds: null, statusText: batch.statusText,
    };
  }, [navBatches, curBatchId, batch]);
  // Selected cell — drives the name box + formula bar.
  const [selCell, setSelCell] = useState<{ col: string; row: number } | null>(null);

  // §13 two-tier notifications (BO UI Standard): route EVERY message through the
  // shared service — success/info render as the top banner; error/warning auto-open
  // the centered blocking modal (NotificationProvider).  No per-form toast.
  const notify = useNotify();
  const showToast = (msg: string, kind: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    notify[kind](msg);
  };
  /** The viewer tried to change a read-only sheet. ALPACA refuses the edit at the
   *  cell (SpreadsheetEntryController wires CellBeginEdit to `e.cancel =
   *  s.cpisedit`, set from the SDMSIsWrite permission) — silently, which leaves
   *  the analyst wondering why nothing happened. We refuse it the same way and
   *  SAY so, ONCE per open: repeating a modal on every click would be worse than
   *  the silence it replaces. */
  const editBlockedShown = useRef(false);
  const onEditBlocked = useCallback(() => {
    if (editBlockedShown.current) return;
    editBlockedShown.current = true;
    void showDialog({
      type: 'warning',
      title: 'Read-only',
      message: preview
        ? 'This is a preview of the SDMS result. Open the package and click Edit to change values.'
        : 'Click Edit before changing values.',
    });
  }, [preview]);
  
  /** A ribbon action the preview does not implement yet.
   *
   *  This is a BLOCKED ACTION, not routine chatter: the analyst clicked something
   *  and nothing happened, so under the two-tier message standard (§18.6 / BO UI
   *  Standard §13) it belongs in the Tier-1 CENTERED MODAL, not the Tier-2 top
   *  banner — a banner auto-dismisses and can scroll away unread. Uses the shared
   *  `showDialog` neutral-blue `info` Notice; no per-form dialog. */
  const showNotWired = (what: string) => {
    void showDialog({
      type: 'info',
      title: 'Not Available Yet',
      message: `${what} is not wired up in this preview.`,
    });
  };

  // Wired sub-popups — same components used elsewhere in this feature.
  const [notesOpen, setNotesOpen]       = useState(false);
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [auditOpen, setAuditOpen]       = useState(false);
  useAuditTrailConfig();
  const auditAllowed = isAuditEnabled('SDMS Result Validation');
  // Shared reason prompt (ALPACA's SpreadsheetEntryController.ShowReason on Update).
  const { prompt } = useConfirm();
  // Keyed by the batch Oid (the notes route key), matching <NotesListPopup batchId>. (Merged 2026-07-07.)
  const notesCount = useNotesCount(curBatch.id ?? '');

  // ── Ribbon picker data (derived from the batch list) ──────────────────
  // Test picker: one row per distinct Test, with Matrix/Method and Sx = count
  // of batches for that test.  QCBatchID picker: batches for the current test.
  const testRows = useMemo<PickRow[]>(() => {
    const counts = new Map<string, number>();
    const meta = new Map<string, { matrix: string; method: string; template: string }>();
    for (const b of navBatches) {
      const t = b.test ?? ''; if (!t) continue;
      counts.set(t, (counts.get(t) ?? 0) + 1);
      // First non-empty template name wins (ALPACA groups by Test → one template
      // name per row).  Falls back to the numeric id only if the name is missing.
      const tplName = b.templateName ?? ((b.templateId ?? 0) > 0 ? String(b.templateId) : '');
      const prev = meta.get(t);
      if (!prev) meta.set(t, { matrix: b.matrix ?? '', method: b.method ?? '', template: tplName });
      else if (!prev.template && tplName) prev.template = tplName;
    }
    return Array.from(counts.keys()).sort((a, b) => a.localeCompare(b)).map(t => {
      const md = meta.get(t)!;
      // Sx = real ALPACA per-test pending-SDMS sample count; batch count is the
      // fallback shown only until the API count arrives.
      return { key: t, test: t, matrix: md.matrix, method: md.method, template: md.template, sx: String(sxCounts[t] ?? counts.get(t) ?? 0) };
    });
  }, [navBatches, sxCounts]);

  // QCBatchID picker — ALL exported QC batches for the job, NOT filtered by the
  // selected Test (ALPACA parity: SpreadsheetEntryController shows every batch;
  // the Status column is the SDMS export status, "Exported").
  const batchRows = useMemo<PickRow[]>(() => {
    const out: PickRow[] = [];
    for (const b of navBatches) {
      const template = b.templateName ?? ((b.templateId ?? 0) > 0 ? String(b.templateId) : '');
      // ALPACA lists ONE row per Job in the batch (a QC batch can span jobs).
      // Split the concatenated jobIds so each job is its own row instead of
      // "260603003;260603002;260603001" crammed into one JobID cell.  All rows
      // of a batch share the same selection key (b.id) but a unique React key.
      const jobs = (b.jobIds ?? '').split(/[;,]/).map(s => s.trim()).filter(Boolean);
      const jobList = jobs.length ? jobs : [''];
      jobList.forEach((jobId, i) => out.push({
        key: b.id, _rk: `${b.id}#${i}`,
        jobId, test: b.test ?? '', matrix: b.matrix ?? '', method: b.method ?? '',
        template, qcBatchId: b.batchId ?? '', status: 'Exported',
      }));
    }
    return out;
  }, [navBatches]);

  const hasNav = navBatches.length > 0;
  const switchTest = (t: string) => {
    setCurTest(t);
    const first = navBatches.find(b => b.test === t);
    if (first) setCurBatchId(first.id);
  };

  // Esc closes the popup (when no sub-popup is open).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !notesOpen && !rollbackOpen && !auditOpen) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, notesOpen, rollbackOpen, auditOpen]);

  // Per-test preset (columns + header rows + data seed) — fallback only.
  const preset = useMemo(() => presetFor(batch.test), [batch.test]);
  const model  = useMemo(() => buildSheet1Model(batch, preset), [batch, preset]);

  // LIVE workbook — the real ALPACA .xlsx for this batch (raw bytes) + parsed
  // grid.  status: 'loading' → fetching/parsing; 'live' → real sheet parsed;
  // 'fallback' → 204/none/parse-fail, show the preset table.
  const [wbBuf, setWbBuf]     = useState<ArrayBuffer | null>(null);
  const [liveGrid, setLiveGrid] = useState<LiveGrid | null>(null);
  const [wbState, setWbState] = useState<{
    status: 'loading' | 'live' | 'fallback';
    source: string;          // saved | template | none
    templateName: string;
  }>({ status: 'loading', source: '', templateName: '' });

  // Fetch the workbook bytes for the CURRENT batch (switchable via the
  // QCBatchID picker) — re-runs whenever curBatchId changes.
  useEffect(() => {
    let cancelled = false;
    setWbState({ status: 'loading', source: '', templateName: '' });
    setWbBuf(null); setLiveGrid(null);
    (async () => {
      try {
        const res = await api.get(
          `/api/v1/analysis/result-validation/${encodeURIComponent(curBatchId)}/sdms-workbook`,
          { responseType: 'arraybuffer' });
        if (cancelled) return;
        const source = String(res.headers?.['x-sdms-source'] ?? '');
        const templateName = String(res.headers?.['x-sdms-template-name'] ?? '');
        const buf = res.data as ArrayBuffer;
        if (res.status === 204 || !buf || (buf.byteLength ?? 0) === 0) {
          setWbState({ status: 'fallback', source: source || 'none', templateName });
          return;
        }
        const grid = await parseWorkbook(buf);
        if (cancelled) return;
        if (!grid) { setWbState({ status: 'fallback', source: source || 'none', templateName }); return; }
        setWbBuf(buf);
        setLiveGrid(grid);
        setSheetTab(grid.activeSheet);
        setWbState({ status: 'live', source: source || 'saved', templateName });
      } catch (e) {
        if (cancelled) return;
        console.warn('[SDMS] workbook fetch/parse failed for', curBatchId, e);
        setWbState({ status: 'fallback', source: 'error', templateName: '' });
      }
    })();
    return () => { cancelled = true; };
  }, [curBatchId]);

  // Re-parse when the analyst switches sheet tabs (live workbook only).
  useEffect(() => {
    let cancelled = false;
    if (wbState.status !== 'live' || !wbBuf || !liveGrid) return;
    if (sheetTab === liveGrid.activeSheet) return;   // already showing it
    (async () => {
      try {
        const grid = await parseWorkbook(wbBuf, sheetTab);
        if (!cancelled && grid) setLiveGrid(grid);
      } catch (e) { console.warn('[SDMS] sheet re-parse failed', e); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetTab]);

  const showLive = wbState.status === 'live' && !!liveGrid;
  // Univer renders the live workbook (editable); it supplies its own formula
  // bar + sheet tabs, so ours are hidden while it's active.
  const univerLive = showLive && useUniver && !!wbBuf;

  // Parse the Calibration sheet from the buffer so we can overlay the
  // reconstructed calibration-curve chart on the Univer view (Univer drops the
  // embedded Excel chart).  Univer owns its own tabs, so `liveGrid` may reflect a
  // different sheet — parse the Calibration sheet directly by name.
  const sheetNamesKey = (liveGrid?.sheetNames ?? []).join('|');
  useEffect(() => {
    if (!univerLive || !wbBuf) { setCalGrid(null); return; }
    const calName = (liveGrid?.sheetNames ?? []).find(n => /calibration/i.test(n));
    if (!calName) { setCalGrid(null); return; }
    let cancelled = false;
    (async () => {
      try { const g = await parseWorkbook(wbBuf, calName); if (!cancelled && g) setCalGrid(g); }
      catch { /* noop */ }
      // What the embedded chart actually plots — read from the chart part so the
      // reconstruction follows the template instead of guessing at column headings.
      try { const s = await readChartSeries(wbBuf); if (!cancelled) setChartSeries(s); }
      catch { /* the header-match fallback in calibrationData still applies */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [univerLive, wbBuf, sheetNamesKey]);

  // Cell currently shown in the formula bar (name box + formula text).
  const selectedCellInfo = useMemo(() => {
    if (!selCell) return { name: 'A1', text: '' };
    const row = model.find(r => r.n === selCell.row);
    const cell = row?.cells[selCell.col];
    return { name: `${selCell.col}${selCell.row}`, text: cell?.v ?? '' };
  }, [selCell, model]);

  // Status-driven ribbon: Rollback only meaningful for already-validated batches.
  const isValidated = /validated/i.test(curBatch.statusText) && !/pending/i.test(curBatch.statusText);
  // Ribbon enablement is a function of the current Mode (View/Enter), per
  // ALPACA modechange().  Unknown labels default to enabled.
  const ribbonEnabled = (label: string) => {
    // RollBack is meaningful only for an already-VALIDATED batch — a pending
    // batch has nothing to revert, so ALPACA greys it out.  Never while editing.
    if (label === 'RollBack') return mode === 'View' && !isEdit && isValidated;
    // BindGrid / Recalculate are stubbed and intentionally DISABLED in every mode
    // (user request 2026-07-08).
    if (label === 'BindGrid' || label === 'Recalculate') return false;
    // Edit sub-mode (View + Edit clicked): ALPACA disables Retrieve / New
    // Calibration and enables Cancel(Clear) / Update(Edit); every other button
    // keeps its View verdict.
    if (mode === 'View' && isEdit) {
      if (label === 'Retrieve' || label === 'New Calibration') return false;
      if (label === 'Clear' || label === 'Edit') return true;
    }
    return RIBBON_ENABLE[label]?.[mode] ?? true;
  };

  // Update button: pull the edited workbook (.xlsx) from Univer, prompt for a
  // MANDATORY reason (ALPACA parity — SpreadsheetEntryController.ShowReason: the
  // reason is required, an empty reason saves nothing), PUT it back with the
  // reason armed as the X-Audit-Reason header so the audit trail records it
  // (AuditData.Comment), then re-lock to read-only.
  async function saveEdits() {
    const res = await univerRef.current?.getEditedXlsx();
    if (!res || res.bytes.byteLength === 0) { setIsEdit(false); showToast('No changes to save.'); return; }
    const { bytes, changes } = res;
    // What the analyst changed, for the audit summary: their band edits and recalculated results (not hidden helper
    // columns such as 8076 AQ = VALUE(D8), which read as "Method Blank1 AQ9: (blank) → 0"), plus the header / band-less
    // cells (Digestion Date D2, H2 / H3) that never were result rows and so were missing (user report 2026-10-12).
    const audited = [...changes.filter(c => !c.hidden), ...(res.headerChanges ?? [])];
    // Diagnostic + UX: if NOTHING was captured, the cell edit didn't commit — tell
    // the user (press Enter to commit) and DON'T save an unchanged sheet.  This also
    // surfaces the capture count so a screenshot tells us if capture worked.
    if (changes.length === 0 && audited.length === 0) {
      showToast('No changes detected — press Enter after typing a cell, then click Update again.', 'warning');
      return;   // stay in edit mode so the user can retry
    }
    // ALPACA always prompts for a reason on Update (not config-gated) — mandatory.
    const reason = await prompt({
      title: 'Reason for change',
      message: 'Enter a reason for changing this SDMS sheet:',
      placeholder: 'Reason…',
      required: true,
    });
    if (reason == null) return;   // cancelled — stay in edit mode, keep the edits
    // Multipart body: the .xlsx workbook (→ SpreadSheetEntry_AnalyticalBatch.SpreadSheet)
    // PLUS the structured result-cell edits the backend writes back to the result rows
    // (SpreadSheetEntry_SampleInfo), segregation-checked in ONE transaction.
    const form = new FormData();
    form.append('workbook',
      new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      'sdms.xlsx');
    form.append('changes', JSON.stringify(changes.map(c => ({
      sysSampleCode: c.label ?? '', parameter: c.parameter ?? '', field: c.field ?? '',
      old: c.old ?? '', new: c.new ?? '',
    }))));
    // Old/New value summaries for the audit ("LCS1 F8: 1.2164 → 1.22"), sent as
    // headers the API records in AuditData Oldvalue/Newvalue.  Capped for header
    // size; the backend falls back to the byte-count when there are no changes.
    // NOTE: no Content-Type — the browser sets the multipart boundary for FormData.
    const headers: Record<string, string> = {};
    if (audited.length) {
      const oldSummary = audited.map(c => `${c.label ? c.label + ' ' : ''}${c.ref}: ${c.old || '(blank)'}`).join(' | ').slice(0, 1600);
      const newSummary = audited.map(c => `${c.label ? c.label + ' ' : ''}${c.ref}: ${c.new || '(blank)'}`).join(' | ').slice(0, 1600);
      headers['X-Sdms-Old'] = encodeURIComponent(oldSummary);
      headers['X-Sdms-New'] = encodeURIComponent(newSummary);
    }
    try {
      await runWithAuditReason(reason, () =>
        api.put(
          `/api/v1/analysis/result-validation/${encodeURIComponent(curBatchId)}/sdms-workbook`,
          form,
          { headers }));
      setIsEdit(false);
      // The edited cells just landed in SpreadSheetEntry_SampleInfo — the same table
      // Batch Results reads — so that grid is stale the moment this returns. Reload it
      // instead of leaving the analyst to press Refresh (client 2026-08-19).
      onSaved?.();
      // Saving the sheet COMMITS edited analytical results - an audited, hard-to-
      // undo write - so it is a MILESTONE, not routine chatter. Under the two-tier
      // standard (CLAUDE.md 18.6 / BO UI Standard 13) that belongs in the Tier-1
      // CENTRED dialog, which must be acknowledged, rather than the Tier-2 banner
      // that auto-dismisses in ~3.2s and can scroll away unread (client 2026-08-19,
      // same call already made for Rollback). The batch id rides in the highlighted
      // id slot per the milestone-success anatomy.
      void showDialog({
        type: 'success',
        title: 'Saved',
        message: 'The sheet has been saved.',
        idLabel: 'QC Batch ID',
        // curBatchId is the batch Oid (what the API route uses) — show the
        // human-readable QCBatchID the analyst recognises, as Rollback does.
        idValue: curBatch.batchId ?? '',
      });
    } catch (e) {
      // Segregation of duties (No Self-Review) returns HTTP 403 with the message —
      // surface it and STAY in edit mode so the reviewer keeps their edits.
      const err = e as { response?: { status?: number; data?: { error?: string } } };
      const msg = err?.response?.data?.error;
      if (err?.response?.status === 403 && msg) {
        showToast(msg, 'warning');
        return;   // keep edit mode + edits so the user can adjust
      }
      console.warn('[SDMS] workbook save failed', e);
      showToast(msg || 'Save failed — check the API logs.', 'error');
    }
  }

  function dispatchRibbon(label: string, action: string) {
    switch (action) {
      case 'close':    onClose(); return;
      case 'fs':       setFullscreen(f => !f); return;
      case 'notes':    setNotesOpen(true); return;
      case 'audit':
        if (!auditAllowed) { showToast('Audit Trail is disabled for this form.', 'warning'); return; }
        setAuditOpen(true); return;
      case 'rollback':
        if (!isValidated) { showToast('This batch is not validated — nothing to roll back.', 'warning'); return; }
        setRollbackOpen(true); return;
      case 'startEdit':  setIsEdit(true); return;
      case 'cancelEdit':
        // Remount the Univer sheet to reload the original (discards edits) + re-lock.
        setUniverKey(k => k + 1); setIsEdit(false);
        // Cancelling THROWS AWAY the analyst's typing, so it is acknowledged in the
        // Tier-1 centred dialog rather than a banner that fades in ~3.2s (§18.6,
        // client 2026-08-19). Neutral Notice, not a warning: the discard is what
        // the user just asked for, not something that went wrong.
        void showDialog({
          type: 'info',
          title: 'Edit Cancelled',
          message: 'Your changes were discarded.',
        });
        return;
      case 'update':     void saveEdits(); return;
      default:
        showNotWired(label);
    }
  }

  return (
    <div className={`ss-ov${fullscreen ? ' ss-full' : ''}`} role="dialog" aria-label="SDMS spreadsheet">
      {/* ── window title ───────────────────────────────────────────── */}
      <div className="ss-title">
        <i className="ti ti-table" />
        SDMS — {curBatch.batchId ?? '—'}
        <button className="ss-x" onClick={onClose} aria-label="Close">×</button>
      </div>

      {/* Preview (LIMS parity): the bare spreadsheet — no ribbon tabs, no
          ribbon toolbar, no Mode/Test/QCBatchID pickers.  Just the grid. */}
      {!preview && (<>
      {/* ── ribbon tabs (SDMS / File / Home / …) ────────────────── */}
      <div className="ss-rtabs">
        {RIBBON_TABS.map(t => (
          <span key={t}
            className={`ss-rt${ribbonTab === t ? ' ss-rt-active' : ''}`}
            onClick={() => {
              if (t === 'SDMS') { setRibbonTab(t); return; }
              showNotWired(`The ${t} ribbon`);
            }}>{t}</span>
        ))}
      </div>

      {/* ── ribbon (mode + Test/QCBatchID pickers + button groups) ─ */}
      <div className="ss-ribbon">
        <div className="ss-sel">
          <div className="ss-r"><b>Mode</b>
            <select value={mode} onChange={e => setMode(e.target.value as 'View' | 'Enter')}>
              <option>View</option><option>Enter</option>
            </select>
          </div>
          <div className="ss-r"><b>Test</b>
            {hasNav ? (
              <GridDropdown
                valueLabel={curBatch.test ?? ''}
                selectedKey={curTest}
                onSelect={switchTest}
                minWidth={610}
                columns={[
                  { key: 'test', label: 'Test', width: 190 },
                  { key: 'matrix', label: 'Matrix', width: 70 },
                  { key: 'method', label: 'Method', width: 115 },
                  { key: 'template', label: 'TemplateName', width: 165 },
                  { key: 'sx', label: 'Sx', width: 48 },
                ]}
                rows={testRows}
              />
            ) : (
              <select value={batch.test ?? ''} disabled><option>{batch.test ?? ''}</option></select>
            )}
          </div>
          <div className="ss-r"><b>QCBatchID</b>
            {hasNav ? (
              <GridDropdown
                valueLabel={curBatch.batchId ?? ''}
                selectedKey={curBatchId}
                onSelect={setCurBatchId}
                minWidth={735}
                columns={[
                  { key: 'jobId', label: 'JobID', width: 108 },
                  { key: 'test', label: 'Test', width: 122 },
                  { key: 'matrix', label: 'Matrix', width: 62 },
                  { key: 'method', label: 'Method', width: 96 },
                  { key: 'template', label: 'Template', width: 108 },
                  { key: 'qcBatchId', label: 'QCBatchID', width: 138 },
                  { key: 'status', label: 'Status', width: 70 },
                ]}
                rows={batchRows}
              />
            ) : (
              <select value={batch.batchId ?? ''} disabled><option>{batch.batchId ?? ''}</option></select>
            )}
          </div>
        </div>

        {SS_GROUPS.map((grp, gi) => (
          <div className="ss-grp" key={`grp-${gi}`}>
            {grp.map(([label, icon, action]) => {
              // Edit sub-mode swaps two buttons IN PLACE (ALPACA parity):
              // Clear→Cancel, Edit→Update.  Enablement stays keyed on the base
              // label so ribbonEnabled()'s edit rules apply.
              let eLabel = label, eIcon = icon, eAction = action;
              if (mode === 'View' && isEdit) {
                if (label === 'Clear')     { eLabel = 'Cancel'; eIcon = 'ti-circle-x'; eAction = 'cancelEdit'; }
                else if (label === 'Edit') { eLabel = 'Update'; eIcon = 'ti-refresh';  eAction = 'update'; }
              }
              const isNotes  = eAction === 'notes';
              const disabled = !ribbonEnabled(label);
              return (
                <button key={`${label}-${action}`} className="ss-btn"
                  disabled={disabled}
                  onClick={() => dispatchRibbon(eLabel, eAction)}
                  title={disabled ? `${eLabel} — not available` : eLabel}>
                  <span className="ss-gi"><i className={`ti ${eIcon}`} /></span>
                  <span>{isNotes ? `Project Notes (${notesCount})` : eLabel}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      </>)}

      {/* ── formula bar (hidden under Univer — it has its own) ──────── */}
      {!univerLive && (
      <div className="ss-fbar">
        <span className="ss-nm">{selectedCellInfo.name}</span>
        <span className="ss-gl">✕</span>
        <span className="ss-ok">✓</span>
        <span className="ss-fx">{selectedCellInfo.text}</span>
      </div>
      )}

      {/* ── sheet body ─────────────────────────────────────────────── */}
      {/* LIVE: Univer editable spreadsheet (ExcelJS → Univer JSON).  LOADING:
          note.  FALLBACK: the legacy preset table.  The ExcelJS HTML table
          renders only when Univer is off. */}
      {wbState.status === 'loading' && (
        <div className="ss-sheet" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8a93a3', fontSize: 13 }}>
          Loading SDMS workbook…
        </div>
      )}

      {univerLive && wbBuf && (
        <div className="ss-sheet" style={{ overflow: 'hidden', minHeight: 0, padding: 0, position: 'relative' }}>
          <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#8a93a3', fontSize: 13 }}>Loading spreadsheet editor…</div>}>
            <SdmsUniverSheet key={univerKey} ref={univerRef} buf={wbBuf} editable={mode === 'View' && isEdit} onEditBlocked={onEditBlocked} onSheetChange={setUniverActiveSheet} onChartBoxes={setChartBoxes} dfOverlay={dfOverlay} qcBatchId={curBatch.batchId ?? undefined} limsRound />
          </Suspense>
          {/* Univer can't render the embedded Excel calibration chart, so we
              overlay the reconstructed Recharts curve while the Calibration sheet
              is the one Univer is showing — parity with ALPACA's embedded chart. */}
          {/* Sized and placed from the TEMPLATE'S OWN chart anchor, not from CSS.
              The box a fixed rule gave was smaller than ALPACA's (client
              2026-08-24); an embedded chart's size comes from the cells it spans,
              so it differs per template and per column width. Falls back to the
              CSS box when the workbook has no readable drawing. */}
          {/calibration/i.test(univerActiveSheet) && calGrid && (
            <div className="ss-chart-float ss-chart-float-univer"
                 style={chartBoxes[univerActiveSheet]
                   ? { left: chartBoxes[univerActiveSheet]!.left, top: chartBoxes[univerActiveSheet]!.top,
                       width: chartBoxes[univerActiveSheet]!.width, height: chartBoxes[univerActiveSheet]!.height }
                   : undefined}>
              <CalibrationChart grid={calGrid} floating series={chartSeries?.get(univerActiveSheet) ?? null} />
            </div>
          )}
        </div>
      )}

      {showLive && liveGrid && !univerLive && (
        <div className="ss-sheet">
          <div className="ss-canvas">
            <table className="ss-xl">
              <colgroup>
                <col style={{ width: 38 }} />
                {liveGrid.colWidths.map((w, i) => <col key={i} style={{ width: w }} />)}
              </colgroup>
              <thead>
                <tr>
                  <th className="ss-cnr" />
                  {liveGrid.colLetters.map((letter, c) => (
                    <th key={c} className="ss-colh">{letter}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {liveGrid.cells.map((row, r) => (
                  <tr key={r}>
                    <td className="ss-rowh">{liveGrid.rowNums[r]}</td>
                    {row.map((cell, c) => {
                      if (cell === null) return null;   // covered by a merge
                      return (
                        <td key={c} className="ss-cell"
                          colSpan={cell.cs} rowSpan={cell.rs}
                          style={{
                            background: cell.bg,
                            color: cell.color,
                            fontWeight: cell.bold ? 700 : undefined,
                            fontSize: cell.size ? `${cell.size}pt` : undefined,
                            textAlign: cell.align,
                            // Real workbook borders where defined (the data
                            // range's bold grid); undefined sides fall back to
                            // the faint default gridline from .ss-cell.
                            borderTop:    cell.bt,
                            borderRight:  cell.br,
                            borderBottom: cell.bb,
                            borderLeft:   cell.bl,
                            // `pre` keeps explicit \n line-breaks in multi-line
                            // headers but does NOT wrap on spaces, so datetimes
                            // like "04/02/2026 12:14" stay on one line (matches
                            // Excel/LIMS no-wrap behaviour).
                            whiteSpace: 'pre',
                          }}>
                          {cell.text}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Reconstructed calibration curve (ExcelJS can't read the embedded
                Excel chart) — floats OVER the empty grid on the Calibration
                sheet, exactly like ALPACA's embedded chart. */}
            {/calibration/i.test(liveGrid.activeSheet) && (
              <div className="ss-chart-float">
                <CalibrationChart grid={liveGrid} floating />
              </div>
            )}
          </div>
        </div>
      )}

      {wbState.status === 'fallback' && (
      <div className="ss-sheet">
        <table className="ss-xl">
          <thead>
            <tr>
              <th className="ss-cnr" />
              {preset.cols.map(c => (
                <th key={c}
                  className={`ss-colh${selCell?.col === c ? ' ss-selcol' : ''}`}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {model.map(row => (
              <tr key={row.n}>
                <td className={`ss-rowh${selCell?.row === row.n ? ' ss-selrow' : ''}`}>{row.n}</td>
                {(() => {
                  // colspan handling — skip cells that fall under a span.
                  const out: React.ReactNode[] = [];
                  let i = 0;
                  while (i < preset.cols.length) {
                    const c    = preset.cols[i]!;
                    const cell = row.cells[c];
                    if (cell) {
                      const span = cell.span ?? 1;
                      const cls = ['ss-cell',
                        cell.k === 'hdr'   ? 'ss-hdr'   : '',
                        cell.k === 'lbl'   ? 'ss-lbl'   : '',
                        cell.k === 'yel'   ? 'ss-yel'   : '',
                        cell.k === 'title' ? 'ss-title' : '',
                        cell.align === 'right' ? 'ss-num' : '',
                        cell.green ? 'ss-green' : '',
                        cell.red   ? 'ss-red'   : '',
                        selCell && selCell.col === c && selCell.row === row.n ? 'ss-sel' : '',
                      ].filter(Boolean).join(' ');
                      out.push(
                        <td key={c} className={cls} colSpan={span}
                          onClick={() => setSelCell({ col: c, row: row.n })}>
                          {cell.v}
                        </td>,
                      );
                      i += span;
                    } else {
                      const cls = 'ss-cell' + (selCell && selCell.col === c && selCell.row === row.n ? ' ss-sel' : '');
                      out.push(
                        <td key={c} className={cls}
                          onClick={() => setSelCell({ col: c, row: row.n })} />,
                      );
                      i++;
                    }
                  }
                  return out;
                })()}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* ── sheet tabs — hidden under Univer (it renders its own tabs) ── */}
      {!univerLive && (
      <div className="ss-stabs">
        {(showLive && liveGrid ? liveGrid.sheetNames : SHEET_TABS).map(t => (
          <span key={t}
            className={`ss-st${(showLive && liveGrid ? liveGrid.activeSheet : sheetTab) === t ? ' ss-st-active' : ''}`}
            onClick={() => {
              // Live workbook: switching tab re-parses that sheet.
              if (showLive) { setSheetTab(t); return; }
              if (t === 'Sheet1') { setSheetTab(t); return; }
              showNotWired(`The ${t} sheet`);
            }}>{t}</span>
        ))}
        <span className="ss-add" title="New sheet (not wired)"
          onClick={() => showNotWired('Adding a new sheet')}>+</span>
      </div>
      )}


      {/* ── sub-popups (reuse existing flows) — act on the CURRENT batch ── */}
      {notesOpen && (
        <NotesListPopup
          batchId={curBatch.id}
          batchTest={curBatch.test ?? ''}
          batchLabel={curBatch.batchId ?? '—'}
          onClose={() => setNotesOpen(false)}
          onChanged={() => { /* notesCount auto-refreshes via useNotesCount */ }}
        />
      )}
      {rollbackOpen && (
        <RollbackModal
          targets={[{
            id: curBatch.id, batchId: curBatch.batchId, statusText: curBatch.statusText,
            statusId: null, test: curBatch.test, method: curBatch.method,
            templateID: curBatch.templateId, validatedByList: null, validatedDateMax: null,
            submittedByList: null,
            submittedDateMax: null,
          }]}
          onClose={() => setRollbackOpen(false)}
          onConfirmed={(resp) => {
            setRollbackOpen(false);
            // Enhanced Re-analysis / Re-prep: only the selected results left the batch
            // for a queue (re-run in a new QC batch); the batch keeps its status.
            if (resp.partial) {
              const refusal = Object.values(resp.skipped ?? {})[0];
              if (refusal) {
                void showDialog({ type: 'error', title: 'Rollback', message: refusal });
                return;
              }
              onRolledBack?.();
              void showDialog({
                type: 'success',
                title: 'Rolled Back',
                message: `${resp.returned ?? 0} result(s) sent back to the ${resp.queue ?? 'queue'}.`,
                idLabel: 'QC Batch ID',
                idValue: curBatch.batchId ?? '',
              });
              return;
            }
            onRolledBack?.();
            // Rollback REVERSES a completed validation, so it is a MILESTONE, not
            // routine chatter: under the two-tier standard (§18.6 / BO UI Standard
            // §13) it belongs in the Tier-1 CENTERED dialog, which must be
            // acknowledged, rather than a Tier-2 banner that auto-dismisses in
            // ~3.2s and can scroll away unread (client 2026-08-18). The batch id
            // rides in the highlighted id slot instead of being inlined in the
            // message, per the milestone-success anatomy.
            void showDialog({
              type: 'success',
              title: 'Rolled Back',
              message: 'The package has been rolled back for re-validation.',
              idLabel: 'QC Batch ID',
              idValue: curBatch.batchId ?? '',
            });
          }}
        />
      )}
      {auditOpen && auditAllowed && (
        <AuditTrailPopup
          formName="SDMS Result Validation"
          sourceId={curBatch.id}
          onClose={() => setAuditOpen(false)}
        />
      )}
    </div>
  );
}
